import { showConfirm } from '../components/ConfirmDialog';
import ChecklistTab from '../components/ChecklistTab';
import FinanceTab from '../components/FinanceTab';
import FilesTab from '../components/FilesTab';
import SetlistTab from '../components/SetlistTab';
import TechTab from '../components/TechTab';
import DayTab from '../components/DayTab';
import DaysheetViewer from '../components/DaysheetViewer';
import LogisticsTab from '../components/LogisticsTab';
import AdvancingTab from '../components/AdvancingTab';
import ImportShowAIModal from '../components/ImportShowAIModal';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { useWriteGate } from '../lib/writeGate';
import { can, canAnyFinance } from '../lib/permissions';
import {
  apiGetShow, apiGetShows, apiCreateShow, apiUpdateShow, apiGetVenues, apiGetBands, apiGetBand, apiCreateVenue, apiSearchVenues, apiGetVenueContacts,
  apiGetShowGuests, apiCreateShowGuest, apiUpdateShowGuest, apiDeleteShowGuest,
  apiGetShowContacts, apiGetShowDistances, apiGetShowFlights,
  apiGetShowTechNotes, apiGetShowMiscNotes, apiGetShowCategorizedNotes,
  apiGetShowRoadmap,
  apiGetRiders, apiCreateRider, apiUpdateRider, apiDeleteRider, apiRegenerateRider,
  apiGetRiderRoles, apiSetRiderRoles,
  apiGetRiderShareByRider, apiEnableRiderShareByRider,
  apiGetShowAvailabilityConflicts,
  apiGetSetlists,
  apiGetChecklists,
  apiDuplicateChecklist,
  apiGetShowIcs,
  apiDuplicateSetlist,
  apiUpdateSetlist,
  apiGetAccommodations, apiCreateAccommodation, apiUpdateAccommodation, apiDeleteAccommodation,
  apiGetRooming, apiCreateRooming, apiUpdateRooming, apiDeleteRooming,
  apiGetExpenses, apiCreateExpense, apiUpdateExpense, apiDeleteExpense,
  apiGetShowFees, apiCreateShowFee, apiUpdateShowFee, apiDeleteShowFee, apiBulkShowFees,
  apiGetTeam,
  apiGetPdfLayoutEffective,
  apiGetDaysheetLayoutEffective,
  apiGetTours, apiCreateTour,
  type ExpenseDTO, type TeamMemberDTO,
  type ShowFeeDTO,
  type ShowDTO, type VenueDTO, type GuestDTO, type SetlistDTO, type TourDTO,
  type AccommodationDTO,
  type AvailabilityConflict,
} from '../api';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useConfig } from '../hooks/useConfig';
import { buildPdfDoc, resolveImageToDataUrl, stagePlotToPdfImage, reencodeDataUrlToPng } from './RiderPage';
import { resolveLightPlotsForPdf, sharedLightPlots } from '../lib/lightPlots';
import { generatePdfBlobViaServer } from '../lib/pdf';
import { buildChecklistDoc } from '../lib/checklistPdf';
import { apiGetShowChecklists } from '../api';
import { apiGetVenueFiles } from '../api';
import { useAuth } from '../context/AuthContext';
import { exportDaysheetPdf } from '../lib/daysheet';
import { crewRowsFromAssignments, crewRowsFromSelection, personDisplayName, personOfficialName } from '../lib/people';
import { buildDocContent } from './SetlistsPage';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import AutoTextarea from '../components/AutoTextarea';
import AirportInput from '../components/AirportInput';
import PasswordInput from '../components/PasswordInput';
import { listStagePlots, type StagePlotListItem } from '../api/stageplots';
import { resolveBandCurrency } from '../lib/countryCurrency';
import PageHeader from '../components/PageHeader';
import { EVENT_TYPE_OPTIONS, showTypeProfile, showTypeProfileFor, resolveShowStopCity, autoShowName, isAutoShowName } from '../lib/showTypes';

const STATUS_OPTIONS = ['scheduled', 'confirmed', 'completed', 'cancelled'];
// Source unique des types + profils (2026-10-17) : lib/showTypes.ts — promo + studio
// ajoutés, profils par type (venue/onglets), règle « on masque, on ne détruit pas ».

// Fetch a weather summary for a show via Open-Meteo (free, no key). Uses the
// venue lat/lng + the show date. Open-Meteo's free API only covers ~ -92 days
// to +16 days, so this returns a real forecast only when the show date falls
// inside that window; otherwise it returns null and the field stays empty /
// manually editable. Returns a short human string. (2026-08-12)
async function fetchWeather(lat: number | null, lng: number | null, date: string | undefined): Promise<string | null> {
  if (lat == null || lng == null || !date) return null;
  const d = new Date(date + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const daysOut = (d.getTime() - Date.now()) / (24 * 3600 * 1000);
  // Only within the forecast window (16 days ahead) do we trust the result.
  if (daysOut > 16 || daysOut < -92) return null;
  try {
    const dateStr = d.toISOString().slice(0, 10);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
      `&timezone=auto&start_date=${dateStr}&end_date=${dateStr}`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const code = data?.daily?.weather_code?.[0];
    const tmax = data?.daily?.temperature_2m_max?.[0];
    const tmin = data?.daily?.temperature_2m_min?.[0];
    const rain = data?.daily?.precipitation_probability_max?.[0];
    if (tmax == null) return null;
    const desc = weatherCodeText(code);
    const temp = tmin != null && tmin !== tmax
      ? `${Math.round(tmin)}–${Math.round(tmax)}°C`
      : `${Math.round(tmax)}°C`;
    const rainTxt = rain != null ? ` · ${Math.round(rain)}% rain` : '';
    return `${desc}, ${temp}${rainTxt}`;
  } catch { return null; }
}
function weatherCodeText(code: number | undefined): string {
  if (code == null) return '';
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mostly clear';
  if (code === 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code >= 45 && code <= 48) return 'Fog';
  if (code >= 51 && code <= 67) return 'Rain';
  if (code >= 71 && code <= 77) return 'Snow';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code >= 95) return 'Thunderstorm';
  return '';
}

interface FormState {
  name: string;
  band_id: string;
  venue_id: string;
  date: string;
  status: string;
  event_type: string;
  // For a travel day: the show being travelled to (its rider sets the transport).
  destination_show_id?: string | null;
  // How the band travels to this show (van/plane/tourbus/train).
  transport?: string;
  notes: string;
  // Tour this show belongs to (optional — a show may be standalone).
  tour_id?: string | null;
  // ---- Finance (deal side) ----
  guarantee?: number;
  currency?: string;
  fee_type?: string;
  deposit_amount?: number;
  deposit_received?: number;
  payment_status?: string;
  payment_due_date?: string;
  payment_notes?: string;
  per_diem_amount?: number;
  per_diem_people?: number;
  // ---- Itinerary / tour-manager day info ----
  timezone?: string;
  electricity?: string;
  wifi_venue?: string;
  luggage_notes?: string;
  running_order?: string;
  welcome_note?: string;
  currency_note?: string;
  artist_pass?: string;
  passport_reminder?: string;
  stage_size?: string;
  // Festival stage name (the specific stage the band plays, e.g. "Main Stage").
  stage_name?: string;
  currency_rate?: string;
  duration_minutes?: number;
  door_time?: string;
  weather?: string;
  // Flights notes (free text under the flight list, shown on the daysheet).
  flight_notes?: string;
  /** Ville de départ d'un TRAVEL day (2026-10-17). */
  from_city?: string;
  /** Ville d'arrivée d'un TRAVEL day : pré-remplie depuis le show de destination, éditable. */
  to_city?: string;
  /** Aéroport du travel day en mode plane : pré-rempli depuis bands.default_airport, éditable. */
  airport_iata?: string;
}

const EMPTY: FormState = {
  name: '',
  band_id: '',
  venue_id: '',
  date: '',
  status: 'scheduled',
  event_type: '',
  destination_show_id: null,
  transport: '',
  notes: '',
  tour_id: null,
};

export default function ShowDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  // Optional tour to auto-assign a NEW show to (from Tours → shows?tour=).
  const tourParam = searchParams.get('tour');
  const { profiles, activeProfileId } = useProfileContext();
  const { config } = useConfig();
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  const [tours, setTours] = useState<TourDTO[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
    const [realId, setRealId] = useState<string | null>(null);
    // Option C (2026-10-09): "read-first" — an existing show lands on the
    // read-only DaysheetViewer; the full form + tabs are shown only after the
    // user clicks Edit (or immediately for a NEW show, which has nothing to read).
    // Surface viewer v2 (2026-10-20): a click from ShowsPage or the Route map
    // (editor+) arrives with ?edit=1 — the edition view opens DIRECTLY. Viewers
    // (and every other surface) keep the read-first landing; the ✏️ Edit button
    // below stays for everyone.
    const [editing, setEditing] = useState(searchParams.get('edit') === '1');
    const showSummary = !isNew && !editing;
    // LOT S2 — "Import show details (AI)" modal. Opens from the page header
    // (next to Edit) for an existing show the user can write.
    const [aiShowOpen, setAiShowOpen] = useState(false);
    // Bumped after an AI import so the load effect refetches the REAL row from
    // the server (never a local merge that could drift from the DB).
    const [showReloadNonce, setShowReloadNonce] = useState(0);
    // True for exactly one refetch: the page stays mounted (the import modal is
    // still on screen showing what was written) instead of flashing a loader.
    const silentReloadRef = useRef(false);

  // Default currency for expenses & the deal — the band's home currency
  // derived from the band's country (fallback EUR). (P7, dynamic currency)
  // Note: this stays the band's currency for finance; the day-sheet CURRENCY
  // CONVERTER uses source=venue, target=band (handled in ItinerarySection).
  const bandCurrency = resolveBandCurrency(profiles.find((p) => p.id === form.band_id)?.country);

  // CAPABILITY MATRIX for the finance area (chantier C, 2026-09-25): the tab is
  // offered as soon as the member holds ANY `finance.*` capability, and each
  // block inside it is gated on ITS OWN capability — expenses (show expenses),
  // fees (the deal + the per-person musician fees). The API enforces the same
  // rule per route. (Was: a single `finance_access` flag on the whole tab.)
  const bandProfile = profiles.find((p) => p.id === form.band_id);
  const financeAccess = canAnyFinance(bandProfile);
  const canExpenses = can(bandProfile, 'finance.expenses');
  const canFees = can(bandProfile, 'finance.fees');

  // ---- Capability-aware WRITE gate (lot A1, 2026-09-25) ----
  // A `users.role === 'read-only'` account whose BAND role is owner/editor used to
  // get the full editor UI on this page while `server/index.js` refused the write
  // (403 → red « Not saved »). `canWriteShow` freezes everything EXCEPT the two
  // finance surfaces that a read-only account may still hold through the matrix
  // (`finance.expenses`, `finance.fees`) — the server mounts those AHEAD of its
  // global read-only guard. Self-service is untouched (never gated here), and the
  // showcase keeps priority (isShowcaseMode() cuts the write at fetchJSON).
  const writeGate = useWriteGate(form.band_id);
  const canWriteShow = writeGate.canWrite;
  // Checklists V2-B: the signed-in user's linked team member (daysheet perso).
  const { user: authUser } = useAuth();
  const myMemberId = authUser?.team_member_id || null;
  // The server exempts the GUESTLIST for a band editor even under the global
  // read-only ceiling (`canWriteGuests`, server/routes/tour-extra.js:49 — plus,
  // since 2026-11-04, ANY member of the show's band when the show has no rider).
  // Gate it on the wider predicate, never on `canWriteShow`, or the UI freezes a
  // legal write. The demo (showcase) stays frozen at fetchJSON.
  const canWriteGuests = (writeGate.canWriteEditor || !!myMemberId) && !isShowcaseMode();
  const canWriteExpenses = writeGate.canWriteCap('finance.expenses');
  const canWriteFees = writeGate.canWriteCap('finance.fees');

  // guestlist state
  const [guests, setGuests] = useState<GuestDTO[]>([]);
  const [newGuest, setNewGuest] = useState('');

  // Venue creation (inline)
  const [showVenueForm, setShowVenueForm] = useState(false);
  const [newVenue, setNewVenue] = useState<{ name: string; address: string; city: string; zip: string; state: string; country: string; capacity: string; timezone: string; electricity: string; currency: string; phone: string; email: string; website: string }>({ name: '', address: '', city: '', zip: '', state: '', country: '', capacity: '', timezone: '', electricity: '', currency: '', phone: '', email: '', website: '' });
  const [venueSearch, setVenueSearch] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState<any[]>([]);
  const [creatingVenue, setCreatingVenue] = useState(false);
  // LOT 4 (2026-11-04) : un travel / off / studio n'affiche pas le bloc venue par
  // défaut, mais on doit pouvoir lui LIER un lieu à la demande (terminal
  // d'embarquement, hôtel, studio…). Cet état n'est qu'un affichage : rien n'est
  // écrit en base tant que l'utilisateur ne choisit pas une venue.
  const [forceShowVenue, setForceShowVenue] = useState(false);

  // ---- Rider (show-specific tech rider) state ----
  const { musicians } = useMusicians(form.band_id || profiles[0]?.id || '');
  const { crew } = useCrew(form.band_id || profiles[0]?.id || '');
  // Candidate destination shows for a travel day (same band).
  const [allShows, setAllShows] = useState<ShowDTO[]>([]);
  const [rider, setRider] = useState<any | null>(null);
  const [transport, setTransport] = useState<'van' | 'plane' | 'tourbus' | 'train' | ''>('');
  const [selMus, setSelMus] = useState<Set<string>>(new Set());
  const [selCrew, setSelCrew] = useState<Set<string>>(new Set());
  // Crew-planning conflicts (availability/double-book) for the current people
  // selection on this show. Set whenever the selection changes on an existing
  // show; drives the warning banner in the "People on this show" block. (V1)
  const [conflicts, setConflicts] = useState<AvailabilityConflict[]>([]);
  // Tabbed layout: Day Sheet / Logistics / Tech / Setlist / Files / Finance (2026-08-07 refonte).
  const [activeTab, setActiveTab] = useState<'day' | 'logistics' | 'tech' | 'setlist' | 'checklist' | 'files' | 'finance' | 'advancing'>('day');
  // PROFIL PAR TYPE (2026-10-17) : si l'onglet actif disparaît après un changement
  // de type (ex. Tech sur un travel day), on retombe sur Day — jamais d'onglet fantôme.
  useEffect(() => {
    if (!showTypeProfile(form.event_type).tabs.includes(activeTab as never)) setActiveTab('day');
  }, [form.event_type, activeTab]);
  // LOT 4 : l'état « lier un lieu » (travel / off / studio) ne survit pas à un
  // changement de show — chaque jour repart de son propre état.
  useEffect(() => { setForceShowVenue(false); }, [id]);
  // NOM AUTO-GÉNÉRÉ (2026-10-17, user) : travel = `Travel Day (from) - (to)` ;
  // soundcheck/rehearsal/promo = `{Type} — {venue}`. Régénéré quand le contexte
  // change — UN nom tapé à la main n'est JAMAIS écrasé (isAutoShowName).
  useEffect(() => {
    if (!isAutoShowName(form.name)) return;
    const auto = autoShowName(form.event_type, { fromCity: form.from_city, toCity: form.to_city, venueName: venues.find((vv) => String(vv.id) === String(form.venue_id))?.name });
    if (auto && form.name !== auto) setForm((f) => ({ ...f, name: auto }));
  }, [form.event_type, form.from_city, form.to_city, form.venue_id, form.name, venues]);
  // Promoter share for THIS show's rider.
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareHasPassword, setShareHasPassword] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  // Per-show role overrides (rider_roles): memberId -> roles for THIS show.
  // Absent member = band default tags. (2026-08-07)
  const [showRoles, setShowRoles] = useState<Record<string, string[]>>({});
  // Per-show backline provider overrides, held locally BEFORE the rider exists
  // so they can be configured during member selection and injected at generation.
  // Mirrors `riders.requirements.backlineProviders` once the rider is created.
  const [backlineProviders, setBacklineProviders] = useState<Record<string, 'band' | 'venue'>>({});
  const [roleSaving, setRoleSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  // The docDef behind the current preview — passed to <PdfPreview> so every
  // page renders on iOS (iframe shows only page 1). (2026-08-13)
  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [daysheetUrl, setDaysheetUrl] = useState<string | null>(null);
  const [daysheeting, setDaysheeting] = useState(false);
  // The daysheet docDef behind the preview — passed to <PdfPreview> so every
  // page renders on iOS (an iframe shows only page 1). (2026-08-13)
  const [daysheetDoc, setDaysheetDoc] = useState<any | null>(null);
  // Stage plot selection for THIS show's rider (2026-08-11 — was lost in a
  // refactor; the show rider must let you pick the band's stage plot, canvas
  // or imported image, before generating the PDF).
  const [stagePlots, setStagePlots] = useState<StagePlotListItem[]>([]);
  const [selectedStagePlotId, setSelectedStagePlotId] = useState<string | null>(null);
  // Resolved band logo (PNG/JPEG data URL) so the show rider PDF follows the
  // rider layout's logo slot — a raw /static/uploads/*.webp path would 500 the
  // server-side pdfmake. (2026-08-11)
  const [bandLogoUrl, setBandLogoUrl] = useState<string | null>(null);

  // ---- Setlist assignment (1 setlist per show) ----
  const [setlists, setSetlists] = useState<SetlistDTO[]>([]);
  const [setlistId, setSetlistId] = useState<number | null>(null);
  const [playedSetlistId, setPlayedSetlistId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  // ---- Checklist assignment (1 reusable checklist per show) ----
  const [checklists, setChecklists] = useState<any[]>([]);
  const [checklistId, setChecklistId] = useState<number | null>(null);
  // Checklist PDF preview docDef (passed to <PdfPreview> so every page renders
  // on iOS — an iframe shows only page 1). (2026-08-13)
  const [checklistPdfDoc, setChecklistPdfDoc] = useState<any | null>(null);
  const [copyingPlayed, setCopyingPlayed] = useState(false);

  // ---- Finance: expenses + settlement ----
  const [expenses, setExpenses] = useState<ExpenseDTO[]>([]);

  // ---- Finance: musician fees / cachets (per member × show) ----
  // A fee is financial data: the whole section is gated by the `finance.fees`
  // capability (the tab itself by any `finance.*`) — the API also enforces it.
  // Candidate members come from the band's team (the existing `teamMembers`
  // state below — the FK target of team_member_id).
  const [fees, setFees] = useState<ShowFeeDTO[]>([]);

  // Single-field setter for the form (finance uses many small fields).
  const setField = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const addExpense = useCallback(async () => {
    if (!realId || !canWriteExpenses) return;
    try {
      const created = await apiCreateExpense(realId, {
        category: 'other', label: '', amount: 0, currency: form.currency || bandCurrency,
      });
      setExpenses((prev) => [...prev, created]);
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, form.currency, canWriteExpenses]);

  const updateExpense = useCallback(async (x: ExpenseDTO, patchData: Partial<ExpenseDTO>) => {
    // Frozen (lot A1): a global read-only account only writes this surface when
    // the `finance.expenses` capability was granted, otherwise the server 403s.
    if (!canWriteExpenses) return;
    // Optimistic: the row updates instantly, the server call reconciles after.
    setExpenses((prev) => prev.map((e) => (e.id === x.id ? { ...e, ...patchData } : e)));
    if (!realId) return;
    try {
      await apiUpdateExpense(realId, String(x.id), { ...x, ...patchData });
      reportSaveOk('finance');
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, canWriteExpenses]);

  const removeExpense = useCallback(async (expenseId: number) => {
    if (!realId || !canWriteExpenses) return;
    try {
      await apiDeleteExpense(realId, String(expenseId));
      setExpenses((prev) => prev.filter((e) => e.id !== expenseId));
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, canWriteExpenses]);

  // ---- Musician fees / cachets mutations (mirror the expense handlers) ----
  const addFee = useCallback(async (memberId: string) => {
    if (!realId || !memberId || !canWriteFees) return;
    try {
      const created = await apiCreateShowFee(realId, {
        team_member_id: memberId, amount: 0,
        currency: form.currency || bandCurrency, status: 'due',
      });
      setFees((prev) => [...prev.filter((f) => f.id !== created.id), created]);
      reportSaveOk('finance');
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, form.currency, bandCurrency, canWriteFees]);

  const updateFee = useCallback(async (fee: ShowFeeDTO, patchData: Partial<ShowFeeDTO>) => {
    // Frozen (lot A1) unless the `finance.fees` capability is granted.
    if (!canWriteFees) return;
    // Optimistic: the row updates instantly, the server call reconciles after.
    setFees((prev) => prev.map((f) => (f.id === fee.id ? { ...f, ...patchData } : f)));
    if (!realId) return;
    try {
      await apiUpdateShowFee(realId, fee.id, patchData);
      reportSaveOk('finance');
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, canWriteFees]);

  const removeFee = useCallback(async (feeId: number) => {
    if (!realId || !canWriteFees) return;
    try {
      await apiDeleteShowFee(realId, feeId);
      setFees((prev) => prev.filter((f) => f.id !== feeId));
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, canWriteFees]);

  // Bulk: same fee for every active member on the show (all_on_show) or a list.
  const bulkFees = useCallback(async (data: { all_on_show?: boolean; team_member_ids?: string[]; amount: number; currency: string }) => {
    if (!realId || !canWriteFees) return;
    try {
      const res = await apiBulkShowFees(realId, { ...data, status: 'due' });
      setFees((prev) => {
        const byId = new Map(prev.map((f) => [f.id, f]));
        for (const f of res.fees) byId.set(f.id, f);
        return [...byId.values()].sort((a, b) =>
          String(a.member_name || '').localeCompare(String(b.member_name || '')) || a.id - b.id);
      });
      reportSaveOk('finance');
    } catch (err) { reportSaveError('finance', err); }
  }, [realId, canWriteFees]);

  const perDiemTotal = (form.per_diem_amount ?? 0) * (form.per_diem_people ?? 0);

  // Totals are grouped BY CURRENCY on purpose: amounts are never converted, so
  // summing across currencies would produce a meaningless number.
  const expensesByCurrency = expenses.reduce<Record<string, number>>((acc, x) => {
    const cur = x.currency || form.currency || bandCurrency;
    acc[cur] = (acc[cur] || 0) + (x.amount || 0);
    return acc;
  }, {});

  const dealCurrency = form.currency || bandCurrency;
  const netInDealCurrency =
    (form.guarantee ?? 0) - (expensesByCurrency[dealCurrency] || 0) - perDiemTotal;

  // Fee totals, also grouped BY CURRENCY (amounts are never converted).
  const feesByCurrency = fees.reduce<Record<string, number>>((acc, f) => {
    if (f.status === 'cancelled') return acc;
    const cur = f.currency || dealCurrency;
    acc[cur] = (acc[cur] || 0) + (f.amount || 0);
    return acc;
  }, {});

  // ---- Accommodation + rooming list ----
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const [rooming, setRooming] = useState<any[]>([]);
  // Online hotel/address search at the top of the Accommodation section.
  const [accSearch, setAccSearch] = useState('');
  const [accResults, setAccResults] = useState<any[]>([]);
  // Inline editor for the "actually played" setlist.
  const [playedSongs, setPlayedSongs] = useState<any[]>([]);
  const [editingPlayed, setEditingPlayed] = useState(false);
  const [savingPlayed, setSavingPlayed] = useState(false);

  const loadPlayedSongs = useCallback((pid: number | null) => {
    if (pid == null) { setPlayedSongs([]); return; }
    const sl = setlists.find((s) => String(s.id) === String(pid));
    if (sl) setPlayedSongs((sl.songs || []).map((s: any) => ({ ...s })));
  }, [setlists]);

  const savePlayedSongs = useCallback(async () => {
    if (playedSetlistId == null || !form.band_id || !canWriteShow) return;
    setSavingPlayed(true);
    try {
      const ordered = playedSongs
        .map((s, i) => ({ ...s, order: i }))
        .filter((s) => !s.is_separator || (s.title && s.title.trim()));
      await apiUpdateSetlist(form.band_id, String(playedSetlistId), { songs: ordered });
      setEditingPlayed(false);
      // Refresh the cached setlist so the PDF/select reflect changes.
      const refreshed = await apiGetSetlists(form.band_id);
      setSetlists(refreshed);
    } catch (e: any) {
      alert(t('showDetail.savePlayedFailed', { msg: e.message }));
    } finally {
      setSavingPlayed(false);
    }
  }, [playedSetlistId, form.band_id, playedSongs, canWriteShow]);

  // ---- Accommodation + rooming handlers ----
  const addAccommodation = useCallback(async () => {
    if (!realId || !canWriteShow) return;
    const a = await apiCreateAccommodation(realId, { hotel_name: 'New hotel' });
    setAccommodations((prev) => [...prev, a]);
  }, [realId, canWriteShow]);
  // Create a hotel from a selected online result: fills name + address + lat/lng
  // so the automatic distance calculator has what it needs.
  const addAccommodationFromResult = useCallback(async (r: any) => {
    if (!realId || !canWriteShow) return;
    const contacts = await apiGetVenueContacts(r.osm_type, r.osm_id).catch(() => ({}));
    const a = await apiCreateAccommodation(realId, {
      hotel_name: r.name || '',
      address: [r.address, r.zip].filter(Boolean).join(', ') || '',
      zip: r.zip || '',
      city: r.city || '',
      country: r.country || '',
      lat: r.lat || 0,
      lng: r.lng || 0,
      phone: (contacts as any).phone || r.phone || '',
      email: (contacts as any).email || r.email || '',
      website: (contacts as any).website || r.website || '',
    });
    setAccommodations((prev) => [...prev, a]);
    setAccSearch('');
    setAccResults([]);
  }, [realId, canWriteShow]);
  // Online search at the top of the section — queries Nominatim (venues/search).
  // Clicking a result (from the list below) creates the hotel.
  const searchAccommodation = useCallback(async (q: string) => {
    setAccSearch(q);
    if (q.trim().length < 3) { setAccResults([]); return; }
    const r = await apiSearchVenues(q.trim()).catch(() => []);
    setAccResults(r);
  }, []);
  const updateAccommodation = useCallback(async (a: any, patch: any) => {
    if (!realId || !canWriteShow) return;
    const updated = await apiUpdateAccommodation(realId, String(a.id), { ...a, ...patch });
    setAccommodations((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  }, [realId, canWriteShow]);
  const deleteAccommodation = useCallback(async (id: number) => {
    if (!realId || !canWriteShow) return;
    await apiDeleteAccommodation(realId, String(id));
    setAccommodations((prev) => prev.filter((x) => x.id !== id));
    // Also drop rooming rows pointing at this hotel.
    setRooming((prev) => prev.filter((r) => r.accommodation_id !== id));
  }, [realId, canWriteShow]);

  const roomingFor = (memberId?: string | number) =>
    rooming.find((r: any) => r.team_member_id === memberId) || null;

  // Create or update the rooming row for a given member (upsert-on-edit).
  const upsertRooming = useCallback(async (memberId: string | number, patch: any) => {
    if (!realId || !memberId || !canWriteShow) return;
    const existing = rooming.find((r: any) => r.team_member_id === memberId) || null;
    if (existing) {
      const updated = await apiUpdateRooming(realId, String(existing.id), { ...existing, ...patch });
      setRooming((prev) => prev.map((x: any) => (x.id === updated.id ? updated : x)));
    } else {
      const created = await apiCreateRooming(realId, { team_member_id: memberId, ...patch });
      setRooming((prev) => [...prev, created]);
    }
  }, [realId, rooming, canWriteShow]);
  const clearRooming = useCallback(async (memberId: string | number) => {
    if (!realId || !canWriteShow) return;
    const existing = rooming.find((r: any) => r.team_member_id === memberId);
    if (!existing) return;
    await apiDeleteRooming(realId, String(existing.id));
    setRooming((prev) => prev.filter((x: any) => x.id !== existing.id));
  }, [realId, rooming, canWriteShow]);

  // Combined member list for the rooming selector.
  // Source of truth = `team_members` (the people registry), because
  // date_assignments.team_member_id has a FK to it. Saving the rider syncs its
  // musicians/crew into that table, so everyone shows up here — while a person
  // added manually on the Team page (driver, promoter rep…) is available too.
  // Falls back to the rider lists if the team hasn't loaded yet.
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  useEffect(() => {
    if (!form.band_id) { setTeamMembers([]); return; }
    let cancelled = false;
    apiGetTeam({ band: form.band_id })
      .then((t) => { if (!cancelled) setTeamMembers(t); })
      .catch(() => { /* fallback below keeps the selector usable */ });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);

  // Diet lives on the Team identity (team_members), not the rider card. Map
  // team_member id -> { diet, dietNotes, nonFoodAllergies } so the show rider
  // PDF shows the same food + non-food allergies as the band rider (pass
  // dietById to buildPdfDoc). (nonFoodAllergies added 2026-09-04 — was missing,
  // so per-person non-food allergies were empty on the show export.)
  const dietById = useMemo(() => {
    const map: Record<string, { diet?: string; dietNotes?: string; nonFoodAllergies?: string }> = {};
    for (const m of teamMembers) {
      map[m.id] = { diet: m.diet || '', dietNotes: m.diet_notes || '', nonFoodAllergies: m.non_food_allergies || '' };
    }
    return map;
  }, [teamMembers]);

  // Identity sync (same convention as MusicianListPage/CrewListPage): the
  // display name comes from the Team registry (nickname ?? name) when the
  // rider card shares the same member id — a nickname edited on the Team page
  // shows up here too, without rewriting the rider cards. (2026-08-25)
  const teamById = useMemo(() => {
    const map = new Map<string, TeamMemberDTO>();
    for (const m of teamMembers) map.set(m.id, m);
    return map;
  }, [teamMembers]);
  const displayName = (member: { id: string; name?: string }): string =>
    teamById.get(member.id)?.nickname || member.name || '';

  // Management / Advancing contact — the band's manager from the TEAM page
  // (personal info lives on team_members, not on the rider crew cards).
  const [teamManager, setTeamManager] = useState<{ name?: string; email?: string; phone?: string } | null>(null);
  useEffect(() => {
    if (!form.band_id) { setTeamManager(null); return; }
    let cancelled = false;
    apiGetTeam({ band: form.band_id, tags: ['manager'] })
      .then((list) => {
        if (cancelled) return;
        const m = list[0];
        if (m) setTeamManager({ name: m.name || m.real_name || m.nickname || '', email: m.email || '', phone: m.phone || '' });
      })
      .catch(() => { /* non-fatal: fall back to the crew card */ });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);

  // Admin-edited PDF layout (global + band override merged) so the show rider
  // follows the Rider Editor. No row → null → buildPdfDoc defaults.
  const [pdfLayout, setPdfLayout] = useState<any | null>(null);
  useEffect(() => {
    if (!form.band_id) { setPdfLayout(null); return; }
    let cancelled = false;
    apiGetPdfLayoutEffective(form.band_id)
      .then((layout) => { if (!cancelled) setPdfLayout(layout); })
      .catch(() => { /* non-fatal: fall back to defaults */ });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);

  // Admin-edited DAYSHEET layout (global + band override merged) so the show
  // daysheet follows the Daysheet Layout editor. No row → null → defaults.
  const [daysheetLayout, setDaysheetLayout] = useState<any | null>(null);
  useEffect(() => {
    if (!form.band_id) { setDaysheetLayout(null); return; }
    let cancelled = false;
    apiGetDaysheetLayoutEffective(form.band_id)
      .then((layout) => { if (!cancelled) setDaysheetLayout(layout); })
      .catch(() => { /* non-fatal: fall back to defaults */ });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);

  const allMembers = teamMembers.length > 0
    ? teamMembers.map((m) => ({ id: m.id, name: m.nickname || m.name, kind: m.role, defaultAirport: m.default_airport }))
    : [
      ...(musicians || []).map((m: any) => ({ id: m.id, name: m.nickname || m.name, kind: 'musician', defaultAirport: (m as any).default_airport })),
      ...(crew || []).map((c: any) => ({ id: c.id, name: c.nickname || c.name, kind: 'crew', defaultAirport: (c as any).default_airport })),
    ];
  // BAND AND CREW — qui est sur la route pour CETTE date. Le registre complet
  // (nickname / role / tags) n'existe que dans `teamMembers` : `allMembers` n'en
  // garde que { id, name, kind } et perdrait la position. Le contrat nom/position
  // est partagé avec le PDF (src/lib/people.ts), pas ré-implémenté ici.
  const baseRegistry = teamMembers.length > 0
    ? teamMembers
    : [...(musicians || []), ...(crew || [])];
  // Le poste est PAR BAND (2026-11-03) : la carte rider de CE groupe fait foi, le
  // registre ne sert que de repli. Un même personne peut être « mon » chez l'un et
  // « foh » chez l'autre — `team_members.role` ne peut pas porter les deux.
  const crewRegistry = baseRegistry.map((m: any) => {
    const card = (crew as any[]).find((c) => String(c?.id) === String(m.id));
    // La CARTE du band fait foi, tags compris : une carte sans tag signifie « aucun
    // rôle de band » (cf. show-specific-roles.md : absence de ligne rider_roles =
    // tags du band), pas « reprends les tags du registre » — sinon les tags globaux
    // de la personne polluent le document du groupe (2026-11-03).
    return card
      ? { ...m, role: card.role || (card.tags || [])[0] || '', tags: card.tags || [] }
      : m;
  });
  // BAND AND CREW = « qui est sur la route ce jour », donc la sélection « People on this
  // show » (rider du show, sinon cartes `main` du groupe). Le rooming ne sert que de
  // repli quand aucune sélection n'existe (2026-11-03 : il était la source, d'où
  // l'absence de Chopiou sur les day sheets KADE / Saint Stacy où il est pourtant
  // sélectionné depuis toujours).
  const selectedPeopleIds = [...selMus, ...selCrew].map(String);
  // Le NOMBRE de personnes varie par show (3 sur une date, 7 sur une autre) : la sélection
  // du show fait foi, y compris quand elle est VIDE alors qu'un rider existe (⇒ personne,
  // section masquée). Le rooming ne sert de repli que pour un show SANS rider ni sélection
  // (données héritées), sinon on réafficherait des gens non sélectionnés.
  const useSelection = !!rider || selectedPeopleIds.length > 0;
  // `showRoles` = les rôles définis POUR CE SHOW (rider_roles) : ils gagnent sur la
  // carte du groupe et sur le registre — un rôle peut n'être vrai qu'une date.
  const showPeople = useSelection
    ? crewRowsFromSelection(selectedPeopleIds, crewRegistry as any, showRoles)
    : crewRowsFromAssignments(rooming, crewRegistry as any);

  // Photo « document » de la salle (section venue-photo) : le fichier marqué
  // `category='photo'` de la salle, résolu en data URL PNG/JPEG (pdfmake n'embarque
  // rien d'autre). Aucune photo marquée ⇒ la section disparaît, écran et PDF.
  const [venuePhoto, setVenuePhoto] = useState<{ dataUrl?: string; caption?: string } | null>(null);
  const venueIdForPhoto = form.venue_id ? String(form.venue_id) : '';
  useEffect(() => {
    let cancelled = false;
    if (!venueIdForPhoto) { setVenuePhoto(null); return; }
    (async () => {
      try {
        const files = await apiGetVenueFiles(venueIdForPhoto);
        const photo = files.find((f) => f.category === 'photo');
        if (!photo) { if (!cancelled) setVenuePhoto(null); return; }
        const dataUrl = await resolveImageToDataUrl(photo.url);
        if (!cancelled) {
          // Légende : la libre si renseignée, sinon le nom de la salle.
          const caption = (photo.caption || '').trim() || venues.find((v) => String(v.id) === venueIdForPhoto)?.name || '';
          setVenuePhoto(dataUrl ? { dataUrl, caption } : null);
        }
      } catch {
        if (!cancelled) setVenuePhoto(null);
      }
    })();
    return () => { cancelled = true; };
  }, [venueIdForPhoto, venues]);
  // Identités pour les documents qui engagent une réservation (rooming, vols) :
  // nom PASSEPORT (`NOM, Prénom`) — le nom de scène reste pour BAND AND CREW.
  const memberOptions = crewRegistry.map((m: any) => ({
    id: String(m.id),
    name: personDisplayName(m),
    officialName: personOfficialName(m),
  }));

  // Logistics (transports + rooming) must only show the members SELECTED in
  // "People on this show" (selMus ∪ selCrew). Those ids are the rider-card ids,
  // which match team_members.id (verified: ensureRiderCard shares the id; legacy
  // cards were backfilled). Fall back to the full band list when the selection
  // is empty (new show / still loading) so the Logistics tab isn't wiped while
  // the rider or the safety-net MAIN default is resolving. (2026-08-17)
  const selectedIds = new Set([...selMus, ...selCrew].map(String));
  const logisticsMembers = selectedIds.size > 0
    ? allMembers.filter((m) => selectedIds.has(String(m.id)))
    : allMembers;


  const copyPlannedAsPlayed = useCallback(async () => {
    if (!realId || setlistId == null || !form.band_id || !canWriteShow) return;
    setCopyingPlayed(true);
    try {
      const planned = setlists.find((s) => String(s.id) === String(setlistId));
      const plannedName = planned?.name || 'Setlist';
      const showName = form.name || 'Show';
      const dup = await apiDuplicateSetlist(form.band_id, String(setlistId), `${showName} — ${plannedName}`);
      await apiUpdateShow(realId, { played_setlist_id: Number(dup.id) });
      setPlayedSetlistId(Number(dup.id));
      // Refresh the setlists list so the new copy appears in the "played" select.
      const refreshed = await apiGetSetlists(form.band_id);
      setSetlists(refreshed);
    } catch (e: any) {
      alert(t('showDetail.copyPlayedFailed', { msg: e.message }));
    } finally {
      setCopyingPlayed(false);
    }
  }, [realId, setlistId, form.band_id, canWriteShow]);

  const exportShowIcs = useCallback(async () => {
    if (!realId) return;
    try {
      // The .ics route now requires the show's band secret calendar_token
      // (Team members only). Read it from the authenticated, band-scoped API.
      // (2026-08-21)
      let token: string | undefined;
      if (form.band_id) {
        const band = await apiGetBand(form.band_id);
        token = band?.calendar_token;
      }
      const blob = await apiGetShowIcs(realId, token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `show-${realId}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      alert(t('showDetail.exportIcsFailed', { msg: e.message }));
    }
  }, [realId, form.band_id]);

  const assignSetlist = useCallback(async (sid: number | null) => {
    if (!realId || !canWriteShow) return;
    setAssigning(true);
    try {
      setSetlistId(sid);
      await apiUpdateShow(realId, { setlist_id: sid });
    } catch (e: any) {
      alert(t('showDetail.setlistAssignFailed', { msg: e.message }));
    } finally {
      setAssigning(false);
    }
  }, [realId, canWriteShow]);

  const assignChecklist = useCallback(async (cid: number | null) => {
    if (!realId || !canWriteShow) return;
    setAssigning(true);
    try {
      setChecklistId(cid);
      await apiUpdateShow(realId, { checklist_id: cid });
    } catch (e: any) {
      alert(t('showDetail.checklistAssignFailed', { msg: e.message }));
    } finally {
      setAssigning(false);
    }
  }, [realId, canWriteShow]);

  // Personalize: copy the band checklist into a show-specific editable copy
  // (same pattern as "Copy planned setlist as played"), then assign it.
  const copyChecklistForShow = useCallback(async () => {
    if (!realId || checklistId == null || !form.band_id || !canWriteShow) return;
    setAssigning(true);
    try {
      // API serialises ids as strings ("3"), checklistId is a number — normalise
      // both sides like every other id lookup in this file (see setlists below).
      const planned = checklists.find((c) => String(c.id) === String(checklistId));
      const plannedName = planned?.name || 'Checklist';
      const showName = form.name || 'Show';
      const dup = await apiDuplicateChecklist(form.band_id, String(checklistId), `${showName} — ${plannedName}`);
      await apiUpdateShow(realId, { checklist_id: Number(dup.id) });
      setChecklistId(Number(dup.id));
      const refreshed = await apiGetChecklists(form.band_id);
      setChecklists(refreshed);
    } catch (e: any) {
      alert(t('showDetail.copyChecklistFailed', { msg: e.message }));
    } finally {
      setAssigning(false);
    }
  }, [realId, checklistId, form.band_id, checklists, form.name, canWriteShow]);

  // Build the checklist PDF preview docDef whenever the selected checklist or
  // its items change (rasterized to images by <PdfPreview> on every device).
  useEffect(() => {
    // BUG FIX (2026-10-10): the API serialises checklist ids as strings ("3") while
    // checklistId is a number, so `c.id === checklistId` was ALWAYS false and the PDF
    // preview never rendered (checklistPdfDoc stayed null). Normalise both sides —
    // same pattern as the setlists lookups in this file.
    const cl = checklists.find((c) => String(c.id) === String(checklistId));
    const bandName = profiles.find((p) => p.id === form.band_id)?.name;
    setChecklistPdfDoc(cl ? buildChecklistDoc(bandName, cl) : null);
  }, [checklistId, checklists, form.band_id, profiles]);

  const assignPlayed = useCallback(async (sid: number | null) => {
    if (!realId || !canWriteShow) return;
    setAssigning(true);
    try {
      setPlayedSetlistId(sid);
      await apiUpdateShow(realId, { played_setlist_id: sid });
    } catch (e: any) {
      alert(t('showDetail.playedSetlistAssignFailed', { msg: e.message }));
    } finally {
      setAssigning(false);
    }
  }, [realId, canWriteShow]);
  const [setlistPdfUrl, setSetlistPdfUrl] = useState<string | null>(null);
  // The setlist docDef behind the preview — passed to <PdfPreview> so every
  // page renders on iOS (an iframe shows only page 1). (2026-08-13)
  const [setlistPdfDoc, setSetlistPdfDoc] = useState<any | null>(null);
  const [exportingSetlist, setExportingSetlist] = useState(false);

  const exportSetlistPdf = useCallback(async (): Promise<string | null> => {
    // Prefer the ACTUALLY-played setlist when present.
    const effectiveId = playedSetlistId != null ? playedSetlistId : setlistId;
    if (effectiveId == null) return null;
    const sl = setlists.find((s) => String(s.id) === String(effectiveId));
    const band = profiles.find((p) => p.id === form.band_id);
    if (!sl || !band) return null;
    setExportingSetlist(true);
    try {
      // Show context: name + date appear in the PDF header for this specific show.
      const showCtx = {
        id: Number(realId) || 0,
        name: form.name || '',
        date: form.date ? form.date.slice(0, 10) : '',
      };
      const docDef = buildDocContent(
        { id: band.id, name: band.name, logo: (band as any).logo, image_url: (band as any).image_url, image: (band as any).image },
        sl,
        showCtx as any,
      );
      const url = await generatePdfBlobViaServer(docDef);
      setSetlistPdfUrl(url);
      setSetlistPdfDoc(docDef);
      return url;
    } catch (e: any) {
      alert(t('showDetail.setlistPdfFailed', { msg: e.message }));
      return null;
    } finally {
      setExportingSetlist(false);
    }
  }, [setlistId, playedSetlistId, setlists, profiles, form.band_id, form.name, form.date, realId]);

  // Pre-generate the setlist PDF whenever the effective setlist changes,
  // so the Open/Print buttons are ready without an extra click.
  const effectiveId = playedSetlistId != null ? playedSetlistId : setlistId;
  useEffect(() => {
    if (effectiveId != null && !setlistPdfUrl) {
      exportSetlistPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  const loadRider = useCallback(async (showId: string) => {
    try {
      const r = await apiGetRiders({ showId });
      if (r) {
        setRider(r);
        setTransport(r.transport || '');
        setSelMus(new Set(JSON.parse(r.active_musician_ids || '[]')));
        setSelCrew(new Set(JSON.parse(r.active_crew_ids || '[]')));
        // Seed the local backline override map from the rider's requirements so
        // the Tech tab reflects persisted overrides after a reload.
        try {
          const req = typeof r.requirements === 'string' ? JSON.parse(r.requirements) : (r.requirements || {});
          setBacklineProviders(req.backlineProviders || {});
        } catch { setBacklineProviders({}); }
        apiGetRiderRoles(r.id).then(setShowRoles).catch(() => setShowRoles({}));
        apiGetRiderShareByRider(r.id).then((s) => {
          setShareEnabled(!!s.token);
          setShareToken(s.token || null);
          setShareHasPassword(!!s.hasPassword);
        }).catch(() => {});
      } else {
        setRider(null);
        setShowRoles({});
        setBacklineProviders({});
        setShareEnabled(false);
        setShareToken(null);
        // No rider yet: pre-select the band's MAIN musicians + MAIN crew so a
        // first "Generate rider" starts from the core lineup (2026-08-11).
        // Only fills when the sets are empty — the async safety-net effect may
        // already have pre-selected once musicians/crew finished loading.
        setSelMus((prev) => (prev.size > 0 ? prev : new Set((musicians || []).filter((m: any) => (m.baseRole ?? m.base_role) === 'main').map((m: any) => m.id))));
        setSelCrew((prev) => (prev.size > 0 ? prev : new Set((crew || []).filter((c: any) => (c.baseRole ?? c.base_role) === 'main').map((c: any) => c.id))));
      }
    } catch {
      setRider(null);
      setShowRoles({});
      setBacklineProviders({});
      setShareEnabled(false);
      setShareToken(null);
      setSelMus((prev) => (prev.size > 0 ? prev : new Set((musicians || []).filter((m: any) => (m.baseRole ?? m.base_role) === 'main').map((m: any) => m.id))));
      setSelCrew((prev) => (prev.size > 0 ? prev : new Set((crew || []).filter((c: any) => (c.baseRole ?? c.base_role) === 'main').map((c: any) => c.id))));
    }
  }, [musicians, crew]);

  useEffect(() => {
    apiGetVenues().then(setVenues).catch(() => {});
    apiGetBands().then(() => {}).catch(() => {});
  }, []);

  // Safety net for the MAIN-member default. Because useMusicians/useCrew load
  // async, they can temporarily hold the PREVIOUS band's members (profiles[0]
  // fallback) while the show's own band is still resolving, and can arrive in
  // more than one tick. So the net must (1) DROP any checked member that isn't
  // in the CURRENT band's lists, and (2) ALWAYS add that band's MAIN members —
  // union, not "keep-if-nonempty-or-fill", otherwise a partial crew load locks
  // the list at the first subset and never adds the rest.
  // (2026-08-11 — v160 reset insufficient; v161 "keep if non-empty" still
  // missed members that arrived in a later tick; v162 = union.)
  useEffect(() => {
    if (rider) return;
    const musIds = new Set((musicians || []).map((m: any) => m.id));
    const crewIds = new Set((crew || []).map((c: any) => c.id));
    const musMains = new Set((musicians || []).filter((m: any) => (m.baseRole ?? m.base_role) === 'main').map((m: any) => m.id));
    const crewMains = new Set((crew || []).filter((c: any) => (c.baseRole ?? c.base_role) === 'main').map((c: any) => c.id));
    setSelMus((prev) => new Set([...[...prev].filter((id) => musIds.has(id)), ...musMains]));
    setSelCrew((prev) => new Set([...[...prev].filter((id) => crewIds.has(id)), ...crewMains]));
  }, [rider, musicians, crew]);

  // Keep the tech-rider transport in sync with the show card's transport.
  // The show's "how we travel" drives which tech preset the rider uses, so
  // picking transport on the card must propagate to the Tech tab and to rider
  // generation (2026-08-13). Only when a value is chosen — a travel day with an
  // empty transport keeps the rider's own default.
  useEffect(() => {
    if (form.transport) setTransport(form.transport as 'van' | 'plane' | 'tourbus' | 'train');
  }, [form.transport]);

  // Single handler for both transport selectors (card + tech panel): keeps the
  // show's transport and the rider-generation transport identical.
  const changeTransport = useCallback((x: string) => {
    if (!x) {
      // Clearing the card's transport (— unspecified —): reset the show form
      // but leave the rider's transport state untouched — a travel day with no
      // transport keeps the rider's own default (same guard as the load effect
      // below). (2026-08-17)
      setForm((f) => ({ ...f, transport: '' }));
      return;
    }
    setTransport(x as 'van' | 'plane' | 'tourbus' | 'train');
    setForm((f) => ({ ...f, transport: x }));
  }, []);

  // 'train' is a valid show transport AND a valid rider preset (config sections
  // are keyed van/plane/tourbus/train). Pass it through as-is so the promoter
  // share resolves plane/train transport to venue-provided backline.
  const riderTransport = transport as 'van' | 'plane' | 'tourbus' | 'train';

  // Tab bar reference + handler: switching a show tab also scrolls the tab bar
  // back to the top of the window, so the selected tab is always visible after
  // scrolling deep inside a long tab's content. (2026-08-13)
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const selectTab = useCallback((t: 'day' | 'logistics' | 'tech' | 'setlist' | 'checklist' | 'files' | 'finance' | 'advancing') => {
    setActiveTab(t);
    // The tab bar is sticky (top:0), so bring it to the top of the window when
    // switching. Use an INSTANT jump (not smooth) so short-content tabs
    // (Setlist/Checklist/Files) land at the same place instead of stopping mid-
    // animation. window.scrollTo with an absolute target is more reliable than
    // scrollIntoView here. (2026-08-14)
    requestAnimationFrame(() => {
      if (tabBarRef.current) {
        const top = tabBarRef.current.getBoundingClientRect().top + window.scrollY - 12;
        window.scrollTo({ top, behavior: 'auto' });
      }
    });
  }, []);

  // Load tours for the show's band (options for the Tour select).
  useEffect(() => {
    if (!form.band_id) {
      setTours([]);
      return;
    }
    apiGetTours()
      .then((all) => setTours(all.filter((t) => !t.band_id || t.band_id === form.band_id)))
      .catch(() => setTours([]));
  }, [form.band_id, canWriteShow]);

  // Load the band's stage plots (canvas + imported images) so the show rider
  // can pick which one to embed. (2026-08-11)
  useEffect(() => {
    if (!form.band_id) { setStagePlots([]); return; }
    let cancelled = false;
    listStagePlots({ bandId: form.band_id })
      .then((res) => {
        if (cancelled) return;
        setStagePlots(res.items || []);
        setSelectedStagePlotId((prev) => prev ?? (res.items?.[0]?.id ?? null));
      })
      .catch(() => { if (!cancelled) setStagePlots([]); });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);

  // Ville d'accueil du groupe (option « Home » du sélecteur Destination d'un
  // travel day) — lue une fois, jamais bloquante (2026-11-03).
  const [bandHomeCity, setBandHomeCity] = useState<string>('');
  useEffect(() => {
    if (!form.band_id) { setBandHomeCity(''); return; }
    let cancelled = false;
    apiGetBand(form.band_id)
      .then((b: any) => { if (!cancelled) setBandHomeCity(String(b?.city || '').trim()); })
      .catch(() => { if (!cancelled) setBandHomeCity(''); });
    return () => { cancelled = true; };
  }, [form.band_id]);

  // Load + resolve the band logo for the show rider PDF (webp→PNG data URL).
  useEffect(() => {
    if (!form.band_id) { setBandLogoUrl(null); return; }
    let cancelled = false;
    apiGetBand(form.band_id)
      .then(async (band: any) => {
        if (cancelled) return;
        const logo = band?.image_url || band?.logo || band?.image || null;
        if (!logo) { setBandLogoUrl(null); return; }
        if (logo.startsWith('data:image/png') || logo.startsWith('data:image/jpeg')) { setBandLogoUrl(logo); return; }
        if (logo.startsWith('data:')) {
          // non-PNG/JPEG data URL → pdfmake can't embed it; re-encode to PNG.
          try {
            const url = await reencodeDataUrlToPng(logo);
            setBandLogoUrl(url ?? null);
          } catch { setBandLogoUrl(null); }
          return;
        }
        setBandLogoUrl((await resolveImageToDataUrl(logo)) ?? null);
      })
      .catch(() => { if (!cancelled) setBandLogoUrl(null); });
    return () => { cancelled = true; };
  }, [form.band_id, canWriteShow]);
  // Create a tour for this band and select it for the show.
  const handleCreateTour = useCallback(async () => {
    if (!canWriteShow) return;
    const name = window.prompt('New tour name');
    if (!name || !name.trim() || !form.band_id) return;
    try {
      const created = await apiCreateTour({ name: name.trim(), band_id: form.band_id });
      setTours((prev) => [...prev.filter((t) => t.id !== created.id), created]);
      setForm((f) => ({ ...f, tour_id: created.id }));
    } catch (e: any) {
      alert(t('showDetail.tourCreateFailed', { msg: e.message }));
    }
  }, [form.band_id, canWriteShow]);

  useEffect(() => {
    if (isNew) {
      // 2026-11-03 : le groupe du NOUVEAU show est celui SÉLECTIONNÉ dans le menu
      // principal (`activeProfileId`), pas le premier de la liste — c'était un vrai
      // bug : ouvrir « New show » depuis SAMAEL créait le show chez KADEBOSTANY.
      const active = activeProfileId || profiles[0]?.id || '';
      // When arriving from the Tours page (?tour=<id>), pre-fill the show's
      // band AND tour so the new show is auto-assigned to that tour.
      if (tourParam) {
        apiGetTours()
          .then((all) => {
            const t = all.find((x: any) => String(x.id) === tourParam);
            setForm({ ...EMPTY, band_id: t?.band_id || active, tour_id: t ? String(t.id) : null });
          })
          .catch(() => setForm({ ...EMPTY, band_id: active, tour_id: tourParam || null }));
        setLoaded(true);
        setLoadError(null);
        return;
      }
      setForm({ ...EMPTY, band_id: active });
      setLoaded(true);
      setLoadError(null);
      return;
    }
    if (!id) return;
    // An AI import refetches the row AFTER the write: keep the page (and the
    // modal that is showing its outcome) mounted instead of flashing a loader.
    if (!silentReloadRef.current) setLoaded(false);
    silentReloadRef.current = false;
    setLoadError(null);
    // When switching to a DIFFERENT show, clear any selection carried over from
    // the previous one (same component instance). loadRider then re-applies the
    // MAIN-member default (or the rider's own selection) from scratch — without
    // this, the safety-net kept the previous band's checked boxes because it
    // never overwrites a non-empty selection. (2026-08-11)
    setSelMus(new Set());
    setSelCrew(new Set());
    apiGetShow(id)
      .then((s: ShowDTO) => {
        setRealId(s.id);
        setForm({
          name: s.name || '',
          band_id: s.band_id || '',
          venue_id: s.venue_id != null ? String(s.venue_id) : '',
          date: s.date ? s.date.slice(0, 10) : '',
          status: s.status || 'scheduled',
          event_type: s.event_type || '',
          destination_show_id: s.destination_show_id || null,
          transport: s.transport || '',
          notes: s.notes || '',
          tour_id: s.tour_id || null,
          guarantee: s.guarantee ?? 0,
          currency: s.currency || resolveBandCurrency(profiles.find((p) => p.id === s.band_id)?.country),
          fee_type: s.fee_type || 'guarantee',
          deposit_amount: s.deposit_amount ?? 0,
          deposit_received: s.deposit_received ?? 0,
          payment_status: s.payment_status || 'unpaid',
          payment_due_date: s.payment_due_date || '',
          payment_notes: s.payment_notes || '',
          per_diem_amount: s.per_diem_amount ?? 0,
          per_diem_people: s.per_diem_people ?? 0,
          // Day-sheet fields (ItinerarySection reads them from `form`)
          welcome_note: s.welcome_note || '',
          timezone: s.timezone || '',
          electricity: s.electricity || '',
          wifi_venue: s.wifi_venue || '',
          currency_note: s.currency_note || '',
          luggage_notes: s.luggage_notes || '',
          running_order: s.running_order || '',
          artist_pass: s.artist_pass || '',
          passport_reminder: s.passport_reminder || '',
          stage_size: s.stage_size || '',
          stage_name: s.stage_name || '',
          currency_rate: s.currency_rate || '',
          duration_minutes: s.duration_minutes ?? 0,
          door_time: s.door_time || '',
          weather: s.weather || '',
          flight_notes: s.flight_notes || '',
          from_city: s.from_city || '',
          to_city: s.to_city || '',
          airport_iata: s.airport_iata || '',
        });
        apiGetShowGuests(s.id).then(setGuests).catch(() => {});
        loadRider(s.id);
        if (s.band_id) {
          apiGetShows({ band: s.band_id }).then(setAllShows).catch(() => {});
          setSetlistId(s.setlist_id != null ? Number(s.setlist_id) : null);
          setPlayedSetlistId(s.played_setlist_id != null ? Number(s.played_setlist_id) : null);
          setChecklistId(s.checklist_id != null ? Number(s.checklist_id) : null);
          apiGetSetlists(s.band_id).then(setSetlists).catch(() => {});
          apiGetChecklists(s.band_id).then(setChecklists).catch(() => {});
          apiGetAccommodations(s.id).then(setAccommodations).catch(() => {});
          apiGetRooming(s.id).then(setRooming).catch(() => {});
          apiGetExpenses(s.id).then(setExpenses).catch(() => {});
          // Musician fees are finance-gated: only fetch when the user holds the
          // `finance.fees` capability (a 403 otherwise — the API enforces it too).
          if (s.band_id && can(profiles.find((p) => p.id === s.band_id), 'finance.fees')) {
            apiGetShowFees(s.id).then(setFees).catch(() => {});
          }
        }
      })
      .catch((e: any) => {
        setLoadError(e?.message || t('showDetail.failedLoadShow'));
        reportSaveError('show', e);
      })
      .finally(() => setLoaded(true));
  }, [id, isNew, profiles, activeProfileId, tourParam, showReloadNonce]);

  // Persist locally-held role overrides to a freshly created rider. Called right
  // after apiCreateRider so roles configured during member selection (before the
  // rider existed) are not lost. Empty lists are skipped (band default).
  const persistRolesToRider = useCallback(async (riderId: string) => {
    if (!canWriteShow) return;
    for (const [memberId, roles] of Object.entries(showRoles)) {
      if (!roles.length) continue;
      try { await apiSetRiderRoles(riderId, memberId, roles); }
      catch (e: any) { reportSaveError('show', e); }
    }
  }, [showRoles, canWriteShow]);

  // Keep the show's rider in sync with the selected members. When members are
  // chosen (selMus / selCrew), ensure a rider exists for THIS show and carries
  // that selection — so the itinerary reflects exactly who is on the road.
  const linkRiderToShow = useCallback(async (showId: string, bandId: string) => {
    const hasSelection = selMus.size > 0 || selCrew.size > 0;
    if (!showId || !bandId || !canWriteShow) return;
    try {
      if (rider) {
        // Rider already linked to this show -> update its selection.
        const updated = await apiUpdateRider(rider.id, {
          transport: riderTransport,
          activeMusicianIds: [...selMus],
          activeCrewIds: [...selCrew],
        });
        setRider(updated);
      } else if (hasSelection) {
        // No rider yet -> create one and point the show at it.
        const created = await apiCreateRider({
          bandId,
          showId,
          transport: riderTransport,
          activeMusicianIds: [...selMus],
          activeCrewIds: [...selCrew],
          backlineProviders,
        });
        setRider(created);
        await persistRolesToRider(created.id);
      }
      // If no selection and no rider, leave the show without a rider.
    } catch (e: any) {
      // Non-fatal: the show itself saved; rider link is best-effort.
      reportSaveError('show', e);
    }
  }, [rider, transport, selMus, selCrew, backlineProviders, persistRolesToRider, canWriteShow]);

  const save = useCallback(async () => {
    if (!form.band_id) {
      alert(t('showDetail.selectBandFirst'));
      return;
    }
    // Frozen (lot A1): the whole show write (create / update) is refused by the
    // server for a global read-only account — never fire it from the client.
    if (!canWriteShow) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name || undefined,
        band_id: form.band_id,
        venue_id: form.venue_id || undefined,
        date: form.date ? new Date(form.date).toISOString() : undefined,
        status: form.status,
        event_type: form.event_type || undefined,
        destination_show_id: form.destination_show_id || null,
        transport: form.transport || undefined,
        notes: form.notes || undefined,
        from_city: form.from_city || undefined,
        to_city: form.to_city || undefined,
        airport_iata: form.airport_iata || undefined,
        tour_id: form.tour_id || null,
        // ---- Finance (deal) columns ----------------------------------------
        // These are `tour_dates` columns written through THIS same route. Two
        // server rules bound them, both read in server/routes/shows.js:
        //  * PUT pins them to their CURRENT value for a caller without
        //    `finance.fees`, and (l.166-172) REJECTS the whole request (403) if
        //    such a caller sends a deal value that DIFFERS from the stored one.
        //    GET also strips them for that caller (l.18-25), so the form holds
        //    0/'' and a blind include would 403 every plain editor's save.
        //  * POST (l.129-135) resets them to defaults at creation for a
        //    non-finance caller — so we do not send them on CREATE at all and
        //    let the server apply its defaults (a finance holder sets the deal
        //    on the show page right after the create redirect).
        // Types: numeric columns go as NUMBERS (empty ⇒ 0, like the loader
        // above); text columns go as real STRINGS ('' — never undefined/null,
        // which would NULL-wipe a stored currency/status on UPDATE).
        ...(!isNew && canFees ? {
          guarantee: form.guarantee ?? 0,
          currency: form.currency || '',
          fee_type: form.fee_type || '',
          deposit_amount: form.deposit_amount ?? 0,
          deposit_received: form.deposit_received ?? 0,
          payment_status: form.payment_status || '',
          payment_due_date: form.payment_due_date || '',
          payment_notes: form.payment_notes || '',
          per_diem_amount: form.per_diem_amount ?? 0,
          per_diem_people: form.per_diem_people ?? 0,
        } : {}),
      };
      if (isNew) {
        const created = await apiCreateShow(payload);
        // Link a rider to the newly created show in one step.
        await linkRiderToShow(String(created.id), String(created.band_id));
        navigate(`/tour/shows/${created.slug || created.id}`, { replace: true });
      } else if (realId) {
        await apiUpdateShow(realId, payload);
        await linkRiderToShow(realId, form.band_id);
        navigate('/tour/shows');
      }
      reportSaveOk('show');
    } catch (e: any) {
      alert(t('showDetail.saveFailed', { msg: e.message }));
      reportSaveError('show', e);
    } finally {
      setSaving(false);
    }
  }, [form, isNew, id, navigate, canWriteShow, canFees, linkRiderToShow]);

  // ---- Auto-save the core show fields (name / date / status / event type /
  // notes / venue) as you type, like the team cards. The explicit Save button
  // above still exists (it also navigates / creates new). The debounce avoids
  // hammering the API while typing. (2026-08-12)
  const lastAutoSave = useRef<string>('');
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isNew || !realId || !canWriteShow) return;
    const core = JSON.stringify({
      name: form.name, date: form.date, status: form.status,
      event_type: form.event_type, destination_show_id: form.destination_show_id,
      transport: form.transport, notes: form.notes, venue_id: form.venue_id,
      from_city: form.from_city, to_city: form.to_city, airport_iata: form.airport_iata,
      tour_id: form.tour_id || null,
      // Finance (deal) columns — same two server rules as the Save payload
      // above: only a caller the server accepts them from (`finance.fees`) may
      // send them, and they must be typed (numbers as numbers, text as a real
      // string) so the UPDATE never NULL-wipes a stored currency/status.
      ...(canFees ? {
        guarantee: form.guarantee ?? 0,
        currency: form.currency || '',
        fee_type: form.fee_type || '',
        deposit_amount: form.deposit_amount ?? 0,
        deposit_received: form.deposit_received ?? 0,
        payment_status: form.payment_status || '',
        payment_due_date: form.payment_due_date || '',
        payment_notes: form.payment_notes || '',
        per_diem_amount: form.per_diem_amount ?? 0,
        per_diem_people: form.per_diem_people ?? 0,
      } : {}),
    });
    if (core === lastAutoSave.current) return;
    lastAutoSave.current = core;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      apiUpdateShow(realId, JSON.parse(core)).then(() => reportSaveOk('show')).catch((e: any) => reportSaveError('show', e));
    }, 500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [form.name, form.date, form.status, form.event_type, form.destination_show_id, form.transport, form.notes, form.venue_id, form.from_city, form.to_city, form.tour_id, form.guarantee, form.currency, form.fee_type, form.deposit_amount, form.deposit_received, form.payment_status, form.payment_due_date, form.payment_notes, form.per_diem_amount, form.per_diem_people, canFees, realId, isNew, canWriteShow]);

  // ---- Venue change: apply the venue's tech data (timezone / electricity /
  // currency / stage size) to the show. Most venues in the catalog don't store
  // timezone/electricity (only online-created ones do), so when the chosen venue
  // lacks them we look them up online (city + country) via /venues/search and
  // derive them. (2026-08-12)
  const handleVenueChange = useCallback(async (vid: string) => {
    const v = venues.find((vv) => String(vv.id) === vid);
    setForm((f) => ({
      ...f,
      venue_id: vid,
      // The show's name defaults to the venue's name when empty (2026-08-13).
      name: f.name.trim() ? f.name : (v?.name || f.name),
      timezone: v?.timezone ? v.timezone : f.timezone,
      electricity: v?.electricity ? v.electricity : f.electricity,
      currency_note: v?.currency ? v.currency : f.currency_note,
      stage_size: v?.stage_size ? v.stage_size : f.stage_size,
    }));
    if (!v) return;
    const patch: Record<string, any> = {};
    if (v.timezone) patch.timezone = v.timezone;
    if (v.electricity) patch.electricity = v.electricity;
    if (v.currency) patch.currency_note = v.currency;
    if (v.stage_size) patch.stage_size = v.stage_size;
    // Derive missing tech from the venue's city + country (online, no key).
    if (!patch.timezone || !patch.electricity) {
      const q = [v.city, v.country].filter(Boolean).join(', ');
      if (q.length >= 3) {
        try {
          const res = await apiSearchVenues(q);
          const hit = (res as any[]).find((x) => x && (x.city || x.name));
          if (hit) {
            if (!patch.timezone && hit.timezone) { patch.timezone = hit.timezone; setForm((f) => ({ ...f, timezone: hit.timezone })); }
            if (!patch.electricity && hit.electricity) { patch.electricity = hit.electricity; setForm((f) => ({ ...f, electricity: hit.electricity })); }
          }
        } catch { /* ignore */ }
      }
    }
    if (realId && Object.keys(patch).length && canWriteShow) {
      apiUpdateShow(realId, patch).then(() => reportSaveOk('show')).catch((e: any) => reportSaveError('show', e));
    }
  }, [venues, realId, canWriteShow]);


  // Toggle one role for a member on THIS show (band default untouched). Before
  // the rider exists the role is held in local state only, then injected at
  // generation; after generation it persists via rider_roles as before.
  const toggleShowRole = useCallback(async (memberId: string, role: string) => {
    if (!canWriteShow) return;
    const current = showRoles[memberId] || [];
    const next = current.includes(role) ? current.filter(r => r !== role) : [...current, role];
    // Optimistic local update, then persist (when a rider exists).
    setShowRoles(prev => ({ ...prev, [memberId]: next }));
    if (!rider) return;
    setRoleSaving(true);
    try {
      await apiSetRiderRoles(rider.id, memberId, next);
    } catch (e: any) {
      console.warn('[ShowDetail] role save failed:', e.message);
      // Revert on failure.
      setShowRoles(prev => ({ ...prev, [memberId]: current }));
    } finally {
      setRoleSaving(false);
    }
  }, [rider, showRoles, canWriteShow]);

  // Reset a member to the band default (removes their rider_roles rows). Held
  // locally before generation, persisted after.
  const resetShowRoles = useCallback(async (memberId: string) => {
    if (!canWriteShow) return;
    const previous = showRoles[memberId];
    setShowRoles(prev => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });
    if (!rider) return;
    setRoleSaving(true);
    try {
      await apiSetRiderRoles(rider.id, memberId, []);
    } catch (e: any) {
      console.warn('[ShowDetail] role reset failed:', e.message);
      if (previous !== undefined) setShowRoles(prev => ({ ...prev, [memberId]: previous }));
    } finally {
      setRoleSaving(false);
    }
  }, [rider, showRoles, canWriteShow]);

  // ---- Promoter share for THIS show's rider ----
  const shareUrl = (() => {
    if (!shareToken) return null;
    return `${window.location.origin}/share/${shareToken}`;
  })();

  const enableShare = useCallback(async () => {
    if (!rider || !canWriteShow) return;
    try {
      const s = await apiEnableRiderShareByRider(rider.id, sharePassword.trim() || undefined);
      setShareEnabled(true);
      setShareToken(s.token);
      setShareHasPassword(!!s.hasPassword || !!sharePassword.trim());
      setSharePassword('');
    } catch (e: any) {
      alert(t('showDetail.shareFailed', { msg: e.message }));
    }
  }, [rider, sharePassword, canWriteShow]);

  const handleCopyShare = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }, [shareUrl]);

  // ---- Guestlist handlers ----
  const addGuest = useCallback(async () => {
    const name = newGuest.trim();
    if (!name || !realId || isNew || !canWriteGuests) return;
    try {
      const g = await apiCreateShowGuest(realId, { name });
      setGuests((prev) => [...prev, g]);
      setNewGuest('');
    } catch (e: any) {
      alert(t('showDetail.addGuestFailed', { msg: e.message }));
    }
  }, [newGuest, realId, isNew, canWriteGuests]);

  // Self-service guestlist from the READ-ONLY Day Sheet view (LOT 2): the viewer
  // adds a name inline through this callback, so `guests` stays the single state.
  const addGuestByName = useCallback(async (name: string, role: string): Promise<GuestDTO | null> => {
    const clean = name.trim();
    if (!clean || !realId || isNew || !canWriteGuests || isShowcaseMode()) return null;
    try {
      const g = await apiCreateShowGuest(realId, role.trim() ? { name: clean, role: role.trim() } : { name: clean });
      setGuests((prev) => [...prev, g]);
      return g;
    } catch (e: any) {
      reportSaveError('show', e);
      return null;
    }
  }, [realId, isNew, canWriteGuests]);

  const updateGuest = useCallback(async (g: GuestDTO, patch: Partial<GuestDTO>): Promise<GuestDTO | null> => {
    if (!realId || !canWriteGuests) return null;
    const updated = await apiUpdateShowGuest(realId, g.id, patch);
    setGuests((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    return updated;
  }, [realId, canWriteGuests]);

  const removeGuest = useCallback(async (g: GuestDTO) => {
    if (!realId || !canWriteGuests) return;
    await apiDeleteShowGuest(realId, g.id);
    setGuests((prev) => prev.filter((x) => x.id !== g.id));
  }, [realId, canWriteGuests]);

  // ---- Venue creation ----
  const createVenue = useCallback(async () => {
    const name = newVenue.name.trim();
    if (!name || !canWriteShow) return;
    setCreatingVenue(true);
    try {
      const v = await apiCreateVenue({
        name,
        address: newVenue.address.trim() || undefined,
        city: newVenue.city.trim() || undefined,
        zip: newVenue.zip.trim() || undefined,
        state: newVenue.state.trim() || undefined,
        country: newVenue.country.trim() || undefined,
        capacity: newVenue.capacity ? parseInt(newVenue.capacity, 10) : undefined,
        timezone: newVenue.timezone.trim() || undefined,
        electricity: newVenue.electricity.trim() || undefined,
        currency: newVenue.currency.trim() || undefined,
      });
      const updated = await apiGetVenues();
      setVenues(updated);
      setForm((f) => ({
        ...f,
        venue_id: String(v.id),
        timezone: !f.timezone && newVenue.timezone ? newVenue.timezone : f.timezone,
        electricity: !f.electricity && newVenue.electricity ? newVenue.electricity : f.electricity,
        currency_note: !f.currency_note && newVenue.currency ? newVenue.currency : f.currency_note,
      }));
      setShowVenueForm(false);
      setNewVenue({ name: '', address: '', city: '', zip: '', state: '', country: '', capacity: '', timezone: '', electricity: '', currency: '', phone: '', email: '', website: '' });
    } catch (e: any) {
      alert(t('showDetail.createVenueFailed', { msg: e.message }));
    } finally {
      setCreatingVenue(false);
    }
  }, [newVenue, canWriteShow]);

  // ---- Rider handlers ----
  // L'autosave de la sélection ne doit se déclencher que sur une ACTION utilisateur :
  // au montage, les listes sont pré-remplies (cartes `main`) et on ne veut pas créer
  // un rider pour chaque show qu'on ouvre (2026-11-03).
  const selectionTouched = useRef(false);
  const toggleMus = useCallback((mid: string) => {
    selectionTouched.current = true;
    setSelMus((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  }, []);

  const toggleCrew = useCallback((cid: string) => {
    selectionTouched.current = true;
    setSelCrew((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  }, []);

  const createRider = useCallback(async () => {
    if (!realId || !form.band_id || !canWriteShow) return;
    try {
      const r = await apiCreateRider({
        bandId: form.band_id,
        showId: realId,
        transport: riderTransport,
        activeMusicianIds: [...selMus],
        activeCrewIds: [...selCrew],
        backlineProviders,
      });
      setRider(r);
      // Inject the role overrides configured during member selection.
      await persistRolesToRider(r.id);
      // A fresh rider has no share yet — reset any stale promoter link so the
      // Advancing section shows "Enable sharing" instead of the old rider's URL.
      setShareEnabled(false);
      setShareToken(null);
      setShareHasPassword(false);
      setSharePassword('');
      reportSaveOk('rider');
    } catch (e: any) {
      alert(t('showDetail.createRiderFailed', { msg: e.message }));
    }
  }, [realId, form.band_id, transport, selMus, selCrew, backlineProviders, persistRolesToRider, canWriteShow]);

  const updateRiderSel = useCallback(async () => {
    if (!rider || !canWriteShow) return;
    try {
      const r = await apiUpdateRider(rider.id, {
        transport: riderTransport,
        activeMusicianIds: [...selMus],
        activeCrewIds: [...selCrew],
      });
      setRider(r);
      reportSaveOk('rider');
    } catch (e: any) {
      alert(t('showDetail.updateRiderFailed', { msg: e.message }));
    }
  }, [rider, transport, selMus, selCrew, canWriteShow]);

  // ---- Update musicians & crew (2026-10-23) — explicit button on the
  // "People on this show" block. The selection itself auto-saves (below), but
  // the rider's CONTENT (requirements) is frozen at generation; after changing
  // the people, this button rebuilds the requirements from the band's CURRENT
  // config + the CURRENT selection/transport (backline provider overrides are
  // preserved server-side). Idempotent: always available while a rider exists.
  // The selection AUTOSAVES within ~600 ms and the rider row moves under us, so
  // a "dirty" gate would flicker and mislead. The button stays ENABLED whenever
  // a rider exists — clicking it is IDEMPOTENT: flush the ticked selection,
  // then rebuild the requirements from it (band config + headcount + transport,
  // backline provider overrides preserved server-side).
  const [regenerating, setRegenerating] = useState(false);
  const updatePeopleOnShow = useCallback(async () => {
    if (!rider || !canWriteShow || !realId) return;
    setRegenerating(true);
    try {
      // Flush the selection first (the regenerate reads the SAVED ids), then rebuild.
      const saved = await apiUpdateRider(rider.id, {
        transport: riderTransport,
        activeMusicianIds: [...selMus],
        activeCrewIds: [...selCrew],
      });
      const r = await apiRegenerateRider(saved.id || rider.id);
      setRider(r);
      reportSaveOk('rider');
    } catch (e: any) {
      reportSaveError('rider', e);
    } finally {
      setRegenerating(false);
    }
  }, [rider, canWriteShow, realId, riderTransport, selMus, selCrew]);

  // ---- Per-item backline provider override for THIS show ----
  // Writes (or clears) an entry in `riders.requirements.backlineProviders`.
  // The EFFECTIVE provider a selected member's item resolves to is computed on
  // the Tech tab via resolveBacklineProvider (override > plane/train > card
  // default); this callback only persists the override the user picks.
  const setBacklineProvider = useCallback(async (itemId: string, provider: 'band' | 'venue' | null) => {
    if (!canWriteShow) return;
    // Update the local override map first — it is the single source of truth for
    // the Tech tab, before AND after generation.
    setBacklineProviders((prev) => {
      const next = { ...prev };
      if (provider === null) delete next[itemId];
      else next[itemId] = provider;
      return next;
    });
    if (!rider) return; // Held locally until the rider is generated.
    let req: Record<string, any> = {};
    if (typeof rider.requirements === 'string') {
      try { req = JSON.parse(rider.requirements); } catch { req = {}; }
    } else {
      req = rider.requirements || {};
    }
    const providers: Record<string, 'band' | 'venue'> = { ...(req.backlineProviders || {}) };
    if (provider === null) delete providers[itemId];
    else providers[itemId] = provider;
    req.backlineProviders = providers;
    try {
      const updated = await apiUpdateRider(rider.id, { requirements: req });
      setRider(updated);
      reportSaveOk('rider');
    } catch (e: any) {
      reportSaveError('rider', e);
    }
  }, [rider, canWriteShow]);

  // ---- Autosave the "People on this show" selection (2026-08-17) ----
  // The selector at the top of the show card only mutates local state
  // (toggleMus / toggleCrew). Previously the selection was ONLY persisted on
  // the explicit Save button (linkRiderToShow) — so checking someone and
  // navigating / letting the core autosave run without hitting Save silently
  // dropped the change (user: "j'avais ajouté Perret mais ça n'a pas
  // enregistré"). This debounced effect persists the selection to the show's
  // rider whenever it changes, like the core-field autosave.
  const lastRiderSelSaved = useRef<string>('');
  useEffect(() => {
    if (isNew || !realId || !canWriteShow) return;
    if (!selectionTouched.current) return; // jamais au montage (cf. selectionTouched)
    const key = JSON.stringify({ m: [...selMus].sort(), c: [...selCrew].sort() });
    if (key === lastRiderSelSaved.current) return;
    lastRiderSelSaved.current = key;
    const t = setTimeout(() => {
      if (!rider) {
        // 2026-11-03 : un show SANS rider gardait sa sélection en mémoire seulement.
        // « Who travels » n'était donc jamais enregistré, et le tour book / l'itinéraire
        // retombaient sur les cartes `main`. On matérialise la sélection en créant le
        // rider du show (le show est l'élément central, comme pour le backline).
        void linkRiderToShow(realId, form.band_id);
        return;
      }
      apiUpdateRider(rider.id, {
        transport: riderTransport,
        activeMusicianIds: [...selMus],
        activeCrewIds: [...selCrew],
      }).then((r) => { setRider(r); reportSaveOk('rider'); }).catch((e: any) => reportSaveError('rider', e));
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selMus, selCrew, rider, realId, isNew, canWriteShow]);

  // ---- Crew-planning conflict warning (V1) ----
  // When the people selection on an existing show changes, ask the server
  // whether any selected member is unavailable on the show date (declared
  // availability) or already double-booked on another show of the same band
  // that same day. The result drives a dismissible banner above the member
  // list. Kept read-only (never blocks the save — it is advisory).
  const conflictTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isNew || !realId) { setConflicts([]); return; }
    const ids = [...selMus, ...selCrew];
    if (!ids.length) { setConflicts([]); return; }
    if (conflictTimer.current) clearTimeout(conflictTimer.current);
    conflictTimer.current = setTimeout(() => {
      apiGetShowAvailabilityConflicts(realId, ids)
        .then((rows) => setConflicts(rows.filter((r) => r.conflicts.length > 0)))
        .catch(() => { /* advisory — never break the page on a network blip */ });
    }, 500);
    return () => { if (conflictTimer.current) clearTimeout(conflictTimer.current); };
  }, [selMus, selCrew, realId, isNew]);

  const deleteRider = useCallback(async () => {
    if (!rider || !canWriteShow) return;
    if (!(await showConfirm(t('showDetail.detachRiderConfirm')))) return;
    try {
      await apiDeleteRider(rider.id);
      setRider(null);
      setSelMus(new Set());
      setSelCrew(new Set());
      // Clear the per-show overrides too, so a fresh rider generated after detach
      // starts from the band defaults rather than the deleted rider's overrides.
      setShowRoles({});
      setBacklineProviders({});
      setShareEnabled(false);
      setShareToken(null);
      setShareHasPassword(false);
      setSharePassword('');
    } catch (e: any) {
      alert(t('showDetail.deleteRiderFailed', { msg: e.message }));
    }
  }, [rider, canWriteShow]);

  // Build the show rider docDef — SHARED by the in-tab preview (openPdf) and
  // the Download PDF button (downloadPdf), so both always render the SAME
  // document (2026-10-23: download added to the Tech tab).
  const buildRiderDocDef = useCallback(async () => {
    if (!form.band_id) return null;
    // The rider may embed backline images served at /static/uploads/...webp
    // (musician/crew photos). Server-side pdfmake can't read a raw path and
    // doesn't support WebP, so resolve them to PNG data URLs first —
    // otherwise the show-rider preview 500s ("File not found in virtual
    // file system"). (2026-08-11)
    const resolveList = async (list: any[] | undefined) =>
      Promise.all((list || []).map(async (item) => ({
        ...item,
        backline: await Promise.all((item.backline || []).map(async (bl: any) => ({
          ...bl,
          imageUrl: bl.imageUrl ? (await resolveImageToDataUrl(bl.imageUrl)) ?? bl.imageUrl : bl.imageUrl,
        }))),
      })));
    const musiciansResolved = await resolveList(musicians as any[]);
    const crewResolved = await resolveList(crew as any[]);
    const riderDoc = rider
      ? {
          transport: riderTransport,
          activeMusicianIds: [...selMus],
          activeCrewIds: [...selCrew],
          requirements: rider.requirements ? JSON.parse(rider.requirements) : null,
        }
      : { transport: riderTransport, activeMusicianIds: [...selMus], activeCrewIds: [...selCrew], requirements: null };
    const selectedPlot = stagePlots.find((p) => p.id === selectedStagePlotId)?.data ?? null;
    const stageImage = selectedPlot ? await stagePlotToPdfImage(selectedPlot) : null;
    // Multi light plots: the SHARED union across the 4 transport modes (same
    // guarded preview → PNG, one image per PDF page, as the band rider page).
    // (2026-10-06)
    const lightPlotImages = await resolveLightPlotsForPdf(
      form.band_id,
      sharedLightPlots((config as any)?.lights),
    );
    return buildPdfDoc(
      profiles.find((p) => p.id === form.band_id)?.name,
      riderDoc as any,
      config as any,
      musiciansResolved as any,
      crewResolved as any,
      selectedPlot,
      stageImage,
      bandLogoUrl,
      dietById,
      showRoles,
      teamManager,
      form.duration_minutes != null && form.duration_minutes > 0 ? form.duration_minutes : null,
      pdfLayout,
      tours.find((t) => t.id === form.tour_id)?.name || null,
      lightPlotImages,
      // Show identity (2026-11-04, user request): {showName} / {showDate} in the
      // rider's version template, header and footer.
      { name: form.name, date: form.date },
    );
  }, [form.band_id, form.duration_minutes, form.tour_id, form.name, form.date, tours, rider, riderTransport, selMus, selCrew, musicians, crew, profiles, config, showRoles, teamManager, pdfLayout, stagePlots, selectedStagePlotId, bandLogoUrl]);

  const openPdf = useCallback(async () => {
    try {
      const docDef = await buildRiderDocDef();
      if (!docDef) return;
      setGenerating(true);
      setPdfDoc(docDef);
    } catch (e: any) {
      alert(t('showDetail.pdfFailed', { msg: e.message }));
    } finally {
      setGenerating(false);
    }
  }, [buildRiderDocDef, t]);

  const downloadPdf = useCallback(async () => {
    try {
      const docDef = await buildRiderDocDef();
      if (!docDef) return;
      const showName = (form.name || 'show').replace(/[^\w\-]+/g, '-').replace(/^-+|-+$/g, '') || 'show';
      // Ask the file NAME first (user 23.10: « ouvrir une fenêtre pour demander
      // le nom ») — pre-filled with the default, Cancel aborts the download.
      const chosen = window.prompt(t('showDetail.pdfNameTitle'), `rider-${showName}.pdf`);
      if (chosen === null) return;
      setGenerating(true);
      const blobUrl = await generatePdfBlobViaServer(docDef);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = chosen.trim() || `rider-${showName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e: any) {
      alert(t('showDetail.pdfFailed', { msg: e.message }));
    } finally {
      setGenerating(false);
    }
  }, [buildRiderDocDef, form.name, t]);

  const exportDaysheet = useCallback(async () => {
    if (!realId) return;
    try {
      setDaysheeting(true);
      // Auto-fill the weather from Open-Meteo (venue coords + show date) at
      // export time, but only when the field is empty and we're within the
      // forecast window — it stays manual otherwise. (2026-08-12)
      if (!form.weather) {
        const venueRow = venues.find((v) => String(v.id) === String(form.venue_id));
        const weather = await fetchWeather(
          venueRow?.lat != null ? Number(venueRow.lat) : null,
          venueRow?.lng != null ? Number(venueRow.lng) : null,
          form.date,
        );
        if (weather) {
          setForm((f) => ({ ...f, weather }));
          if (canWriteShow) apiUpdateShow(realId, { weather }).then(() => reportSaveOk('show')).catch((e: any) => reportSaveError('show', e));
        }
      }
      // Fetch the freshest roadmap at export time — the editable roadmap lives
      // in ItinerarySection (shared table), so the local state can be stale.
      const freshRoadmap = await apiGetShowRoadmap(realId).catch(() => []);
      const contacts = await apiGetShowContacts(realId).catch(() => []);
      const freshRooming = await apiGetRooming(realId).catch(() => []);
      const freshDistances = await apiGetShowDistances(realId).catch(() => []);
      const freshTechNotes = await apiGetShowTechNotes(realId).catch(() => []);
      const freshMiscNotes = await apiGetShowMiscNotes(realId).catch(() => []);
      const freshCategorizedNotes = await apiGetShowCategorizedNotes(realId).catch(() => []);
      // Checklists V2-B: the show's linked checklists (manager PDF lists ALL
      // items). Fetched at export time so the payload is always fresh.
      const showChecklists = form.band_id
        ? await apiGetShowChecklists(form.band_id, realId).catch(() => [])
        : [];
      // Flights for the PDF: resolve each flight's member_ids to display names.
      const rawFlights = await apiGetShowFlights(realId).catch(() => []);
      const flightsForPdf = rawFlights.map((f) => {
        let ids: string[] = [];
        try { ids = JSON.parse((f as any).member_ids || '[]'); } catch { ids = []; }
        if (ids.length === 0 && (f as any).team_member_id) ids = [String((f as any).team_member_id)];
        const names = ids.map((i) => {
          const m = memberOptions.find((mm) => String(mm.id) === String(i));
          return m ? (m.officialName || m.name) : i; // identité passeport d'abord
        }).filter(Boolean);
        return { ...f, member_ids: JSON.stringify(names) };
      });
      const { url, docDef } = await exportDaysheetPdf({
        show: { ...form, id: realId } as ShowDTO,
        roadmap: freshRoadmap,
        guests,
        contacts,
        rooming: freshRooming,
        accommodations,
        flights: flightsForPdf,
        distances: freshDistances,
        techNotes: freshTechNotes,
        miscNotes: freshMiscNotes,
        categorizedNotes: freshCategorizedNotes,
        checklists: showChecklists.map((c: any) => ({
          name: c.name,
          category: c.category,
          items: (c.items || []).map((it: any) => ({
            text: it.text,
            done: !!it.done,
            assignee: it.assignee_id
              ? (allMembers.find((m) => String(m.id) === String(it.assignee_id))?.name || it.assigned_to || '')
              : (it.assigned_to || ''),
          })),
        })),
        people: (rider || [...selectedIds].length > 0)
          ? crewRowsFromSelection([...selectedIds], crewRegistry as any, showRoles)
          : crewRowsFromAssignments(freshRooming, crewRegistry as any),
        members: memberOptions,
        venuePhoto,
        bandName: profiles.find((p) => p.id === form.band_id)?.name,
        venueName: venues.find((v) => String(v.id) === form.venue_id)?.name,
        venue: venues.find((v) => String(v.id) === String(form.venue_id)) || null,
        // LOT 4 (2026-11-04) : le PDF applique la même règle que l'écran —
        // un travel RETOUR (J4) masque électricité / passeport / devise.
        bandHomeCity,
      }, daysheetLayout);
      setDaysheetUrl(url);
      setDaysheetDoc(docDef);
    } catch (e: any) {
      alert(t('showDetail.daysheetPdfFailed', { msg: e.message }));
    } finally {
      setDaysheeting(false);
    }
  }, [realId, form, guests, profiles, venues, daysheetLayout, crewRegistry, memberOptions, venuePhoto, rider, selMus, selCrew, showRoles, bandHomeCity]);

  // LOT 4 (2026-11-04, décision user « 7C ») : les VILLES D'ÉTAPE d'un travel day
  // privilégient la ville de l'HÔTEL (où l'on dort) puis celle de la VENUE
  // (`resolveShowStopCity`). Changer la Destination pré-remplit `to_city` depuis
  // le show d'arrivée et `from_city` depuis le show PRÉCÉDENT (ou la ville de
  // base du groupe) — toujours ÉDITABLE : une valeur déjà saisie n'est jamais
  // écrasée.
  const onDestinationChange = async (rawId: string) => {
    const id = rawId || null;
    // HOME (2026-11-03) : le retour d'un travel day va vers le domicile du
    // groupe — valeur sentinelle 'home' (la colonne est TEXT, aucun JOIN ne
    // matche un show).
    if (id === 'home') {
      setForm((f) => ({ ...f, destination_show_id: 'home', ...(bandHomeCity ? { to_city: bandHomeCity } : {}) }));
      return;
    }
    // ⚠️ les IDs ShowDTO sont des NUMBERS — comparer en String (un `x.id === id`
    // avec id string ne matchait JAMAIS : le bug qui ne remplissait pas le transport).
    const dest = id ? allShows.find((x) => String(x.id) === String(id)) : undefined;
    const patch: Partial<FormState> = { destination_show_id: id };
    if (dest?.transport) patch.transport = dest.transport;
    setForm((f) => ({ ...f, ...patch }));
    if (!id || !dest) return;
    const destAccs = await apiGetAccommodations(String(dest.id)).catch(() => [] as AccommodationDTO[]);
    const toCity = resolveShowStopCity(dest, destAccs, venues);
    const prevShow = [...allShows]
      .filter((x) => String(x.id) !== String(realId) && String(x.id) !== String(dest.id))
      .filter((x) => (x.date || '') <= (form.date || ''))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
    let fromCity = bandHomeCity;
    if (prevShow) {
      const prevAccs = await apiGetAccommodations(String(prevShow.id)).catch(() => [] as AccommodationDTO[]);
      fromCity = resolveShowStopCity(prevShow, prevAccs, venues) || bandHomeCity;
    }
    setForm((f) => ({
      ...f,
      destination_show_id: id,
      ...(toCity && !String(f.to_city || '').trim() ? { to_city: toCity } : {}),
      ...(fromCity && !String(f.from_city || '').trim() ? { from_city: fromCity } : {}),
    }));
  };

  if (!loaded) return <div className="page"><div className="loading">Loading…</div></div>;

  return (
    <div className="page" data-page="show-detail" data-density="socle">
      <PageHeader
        title={isNew ? t('showDetail.newShow') : (form.name || t('showDetail.showDetails'))}
        actions={
          <>
            {!isNew && realId && (
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/tour/show-time/${realId}`)}
                title={t('showDetail.showTimeTitle')}
              >
                ▶ {t('showDetail.showTime')}
              </button>
            )}
            {showSummary && canWriteShow && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                ✏️ {t('showDaySummary.edit')}
              </button>
            )}
            {!isNew && realId && canWriteShow && (
              <button
                className="btn btn-secondary"
                onClick={() => setAiShowOpen(true)}
                title={t('showImport.buttonTitle')}
                data-ai-show-open
              >
                🤖 {t('showImport.button')}
              </button>
            )}
            {editing && !isNew && (
              <button className="btn btn-secondary" onClick={() => setEditing(false)}>
                {t('showDaySummary.done')}
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => navigate('/tour/shows')}>{t('showDetail.back')}</button>
            {!showSummary && (
              <button className="btn btn-primary" onClick={save} disabled={saving || !canWriteShow}>
                {saving ? t('showDetail.saving') : t('showDetail.save')}
              </button>
            )}
          </>
        }
      />

      {/* LOT S2 — AI show-details import. The modal only PROPOSES; every write
          goes through /api/ai/chat/apply with the ticked fields, then the show
          is refetched from the server (nonce) so the page shows the real row. */}
      {aiShowOpen && realId && form.band_id && (
        <ImportShowAIModal
          bandId={form.band_id}
          showId={String(realId)}
          current={form as unknown as Record<string, unknown>}
          canWrite={canWriteShow}
          onClose={() => setAiShowOpen(false)}
          onImported={() => {
            // Keep the modal on screen (it shows "N field(s) filled") and
            // refetch the REAL row underneath, without unmounting the page.
            silentReloadRef.current = true;
            setShowReloadNonce((n) => n + 1);
          }}
        />
      )}

      {/* ONE discreet note (lot A1): same key as the other gated pages, existing
          conventions (dimmed small text, no new colour). A global read-only
          account sees it once at the top of the page; the demo (showcase) is
          explained by the app-wide <ShowcaseBanner/> and gets no local note. */}
      {writeGate.readOnlyReason === 'global-read-only' && !canWriteShow && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {loadError && (
        <div className="card" style={{ maxWidth: 1000, marginBottom: 20, borderColor: 'var(--danger)' }}>
          <p style={{ color: 'var(--danger)' }}>{t('showDetail.failedLoad', { msg: loadError })}</p>
        </div>
      )}

      {/* Option C (2026-10-09): read-first — the day-at-a-glance summary is the
          landing view for an existing show. The full form + tabs are shown only
          after Edit (or immediately for a new show). Read-only users never see
          the editing surface. */}
      {showSummary && (
              <DaysheetViewer
                form={form}
                venue={venues.find((v) => String(v.id) === String(form.venue_id)) || null}
                realId={realId}
                bandName={profiles.find((p) => p.id === form.band_id)?.name}
                guests={guests}
                accommodations={accommodations}
                rooming={rooming}
                members={memberOptions}
                people={showPeople}
                venuePhoto={venuePhoto}
                myMemberId={myMemberId}
                layout={daysheetLayout}
                canWrite={canWriteShow}
                // LOT 4 (2026-11-04) : le viewer applique la règle du travel
                // RETOUR (J4) — électricité / passeport / devise masqués.
                bandHomeCity={bandHomeCity}
                // LOT 2 self-service: the read-only view writes only the member's
                // OWN room / seat / guests (server-enforced from the session).
                canWriteGuests={canWriteGuests}
                onRoomingUpdated={(row) => {
                  // Upsert in the parent state so the row immediately becomes
                  // "mine" (the editor then moves onto the fetched row).
                  setRooming((prev: any[]) => {
                    const idx = prev.findIndex((x: any) => x.id === row.id);
                    if (idx === -1) return [...prev, row];
                    return prev.map((x: any) => (x.id === row.id ? row : x));
                  });
                }}
                onAddGuest={addGuestByName}
                onUpdateGuest={updateGuest}
                onRemoveGuest={removeGuest}
                onEdit={() => setEditing(true)}
              />
            )}

      {!showSummary && (
      <>
      {/* Show core info */}
      <div className="card" style={{ marginBottom: 20 }}>
        {/* LIGNE D'IDENTITÉ en tête (2026-10-17, user) : date / statut / type / transport
            d'abord — le type pilote tout ce qui s'affiche ensuite (venue, onglets, textes). */}
        <div className="form-grid">
          <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
            <label>{t('showDetail.date')}</label>
            <input type="date" className="form-input" value={form.date}
              disabled={!canWriteShow}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
            <label>{t('showDetail.status')}</label>
            <select className="form-input" value={form.status}
              disabled={!canWriteShow}
              onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
            <label>{t('showDetail.eventType')}</label>
            <select className="form-input" value={form.event_type}
              disabled={!canWriteShow}
              onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
              <option value="">{t('showDetail.selectType')}</option>
              {/* Libellés traduits (LOT 4, 2026-11-04) — la VALUE SQL reste
                  intacte ('show', 'travel', 'other', …) ; `other` = journée
                  d'embarquement / sur site sans concert. */}
              {EVENT_TYPE_OPTIONS.map((s: string) => (
                <option key={s} value={s}>{t(`showDetail.eventTypes.${s}`, { defaultValue: s })}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
            <label>{t('showDetail.transport')}</label>
            <select className="form-input" value={form.transport || ''}
              disabled={!canWriteShow}
              onChange={(e) => changeTransport(e.target.value)}>
              <option value="">{t('showDetail.unspecified')}</option>
              <option value="van">🚐 Van</option>
              <option value="tourbus">🚌 Tourbus</option>
              <option value="train">🚆 Train</option>
              <option value="plane">✈️ Plane</option>
            </select>
          </div>
          {form.event_type === 'travel' && (
            <div className="form-field" style={{ flex: 2, minWidth: 220 }}>
              <label>{t('showDetail.destinationShow')}</label>
              <select className="form-input" value={form.destination_show_id || ''}
                disabled={!canWriteShow}
                onChange={(e) => { void onDestinationChange(e.target.value); }}>
                <option value="">{t('showDetail.selectTravelShow')}</option>
                <option value="home">{t('showDetail.homeOption')}</option>
                {/* SHOWS À VENIR seulement (2026-10-17, user) : on voyage VERS un show
                    futur — les shows passés disparaissent de la liste (elle serait énorme). */}
                {allShows
                  .filter((x) => x.id !== realId)
                  .filter((x) => (x.date || '') >= new Date().toISOString().slice(0, 10))
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.date ? new Date(x.date).toLocaleDateString() : ''} — {x.name || x.venue_name || 'show'}
                    </option>
                  ))}
              </select>
              {form.destination_show_id && (
                <small className="text-dim" style={{ display: 'block', marginTop: 4 }}>
                  {form.destination_show_id === 'home'
                    ? `${t('showDetail.homeHint')}${bandHomeCity ? ` — ${bandHomeCity}` : ''}`
                    : t('showDetail.transportIconHint')}
                </small>
              )}
            </div>
          )}
          {form.event_type === 'travel' && (
            <div className="form-field" style={{ flex: 1, minWidth: 160 }}>
              <label>{t('showDetail.fromCity')}</label>
              <input className="form-input" value={form.from_city || ''}
                disabled={!canWriteShow}
                onChange={(e) => setForm({ ...form, from_city: e.target.value })}
                placeholder={t('showDetail.fromCityPh')} />
            </div>
          )}
          {form.event_type === 'travel' && (
            <div className="form-field" style={{ flex: 1, minWidth: 160 }}>
              <label>{t('showDetail.toCity')}</label>
              <input className="form-input" value={form.to_city || ''}
                disabled={!canWriteShow}
                onChange={(e) => setForm({ ...form, to_city: e.target.value })}
                placeholder={t('showDetail.toCityPh')} />
            </div>
          )}
          {/* AÉROPORT (2026-10-17) : proposé par défaut depuis bands.default_airport
              (Band Settings) quand le travel day est en mode plane — éditable. */}
          {form.event_type === 'travel' && form.transport === 'plane' && (
            <div className="form-field" style={{ flex: 1, minWidth: 160 }}>
              <label>{t('bandSettings.defaultAirport')} <small style={{ opacity: 0.7 }}>(IATA)</small></label>
              <AirportInput
                mode="air"
                value={form.airport_iata || ''}
                disabled={!canWriteShow}
                onChange={(v) => setForm({ ...form, airport_iata: v })}
                placeholder={profiles.find((p) => p.id === form.band_id)?.default_airport || t('showDetail.fromCityPh')} />
            </div>
          )}
        </div>
        {/* PROFIL PAR TYPE (2026-10-17, lib/showTypes.ts) + LOT 4 (2026-11-04) :
            le bloc venue a du sens pour les types qui en ont un (show/festival/
            djset/soundcheck/rehearsal/promo) ET dès qu'un lieu est LIÉ sur le
            show (ex. Jour 2 d'une tournée : travel + venue_id = terminal
            d'embarquement) — ou à la demande via « + Link a venue / location ».
            La valeur stockée n'est JAMAIS détruite par un changement de type. */}
        {(showTypeProfileFor(form.event_type, form.venue_id).needsVenue || forceShowVenue) && (
        <div className="form-grid">
<div className="form-field" style={{ flex: 1, minWidth: 200 }}>
            <label>{t('showDetail.venueLabel')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="form-input" value={form.venue_id}
                disabled={!canWriteShow}
                onChange={(e) => { void handleVenueChange(e.target.value); }}>
                <option value="">{t('showDetail.selectVenue')}</option>
                {venues.map((v) => (
                  <option key={v.id} value={String(v.id)}>{v.name}{v.city ? ` (${v.city})` : ''}</option>
                ))}
              </select>
              <button className="btn btn-secondary" type="button"
                onClick={() => setShowVenueForm((s) => !s)} disabled={creatingVenue}>
                {t('showDetail.newVenueBtn')}
              </button>
              {/* Un lieu lié sur un jour qui n'en a pas besoin peut être DÉTACHÉ
                  (le type n'a pas changé : seul le lien disparaît). */}
              {!showTypeProfile(form.event_type).needsVenue && (
                <button className="btn btn-secondary" type="button" data-unlink-venue
                  disabled={!canWriteShow}
                  onClick={() => { setForm((f) => ({ ...f, venue_id: '' })); setForceShowVenue(false); }}>
                  {t('showDetail.unlinkVenue')}
                </button>
              )}
            </div>
            {showVenueForm && (
              <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--border)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Online lookup — search a real venue and auto-fill the fields */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    value={venueSearch}
                    list="inline-venue-search"
                    placeholder={t('showDetail.searchOnline')}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setVenueSearch(q);
                      if (q.trim().length < 3) { setVenueSearchResults([]); return; }
                      try {
                        const results = await apiSearchVenues(q.trim());
                        setVenueSearchResults(results);
                        // If the typed value exactly matches an online result, fill
                        // the venue form (name, address, coords + derived tech fields).
                        const exact: any = results.find((r: any) => r.name === q.trim());
                        if (exact) {
                          const contacts = await apiGetVenueContacts(exact.osm_type, exact.osm_id).catch(() => ({}));
                          setNewVenue((prev) => ({
                            ...prev,
                            name: prev.name || exact.name || '',
                            address: exact.address || prev.address || '',
                            city: exact.city || prev.city || '',
                            zip: exact.zip || prev.zip || '',
                            state: exact.state || prev.state || '',
                            country: exact.country || prev.country || '',
                            timezone: exact.timezone || prev.timezone || '',
                            electricity: exact.electricity || prev.electricity || '',
                            currency: exact.currency || prev.currency || '',
                            phone: (contacts as any).phone || prev.phone || '',
                            email: (contacts as any).email || prev.email || '',
                            website: (contacts as any).website || prev.website || '',
                          }));
                        }
                      } catch { setVenueSearchResults([]); }
                    }}
                  />
                  <datalist id="inline-venue-search">
                    {venueSearchResults.map((r, i) => (
                      <option key={i} value={r.name}>
                        {[r.city, r.country].filter(Boolean).join(', ')} · {r.timezone || ''} {r.currency || ''}
                      </option>
                    ))}
                  </datalist>
                </div>
                <input className="form-input" value={newVenue.name}
                  placeholder={t('showDetail.venueName')} onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })} />
                <input className="form-input" value={newVenue.address}
                  placeholder={t('showDetail.addressStreet')} onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input className="form-input" style={{ flex: 2, minWidth: 120 }} value={newVenue.city}
                    placeholder={t('showDetail.city')} onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })} />
                  <input className="form-input" style={{ width: 90 }} value={newVenue.zip}
                    placeholder={t('showDetail.zip')} onChange={(e) => setNewVenue({ ...newVenue, zip: e.target.value })} />
                  <input className="form-input" style={{ flex: 1, minWidth: 100 }} value={newVenue.state}
                    placeholder={t('showDetail.state')} onChange={(e) => setNewVenue({ ...newVenue, state: e.target.value })} />
                  <input className="form-input" style={{ flex: 1, minWidth: 100 }} value={newVenue.country}
                    placeholder={t('showDetail.country')} onChange={(e) => setNewVenue({ ...newVenue, country: e.target.value })} />
                  <input className="form-input" style={{ width: 110 }} value={newVenue.capacity}
                    placeholder={t('showDetail.capacity')} type="number" onChange={(e) => setNewVenue({ ...newVenue, capacity: e.target.value })} />
                </div>
                <div>
                  <button className="btn btn-primary" type="button" onClick={createVenue}
                    disabled={!newVenue.name.trim() || creatingVenue}>
                    {creatingVenue ? t('showDetail.creating') : t('showDetail.createVenue')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}{/* fin bloc venue conditionnel (profil par type + lieu lié) */}
        {/* LOT 4 (2026-11-04) : sur un travel / off / studio SANS lieu lié, un
            bouton discret permet d'en attacher un à la demande (terminal
            d'embarquement, hôtel, studio) sans changer le type du jour. */}
        {!showTypeProfileFor(form.event_type, form.venue_id).needsVenue && !forceShowVenue && (
          <div className="form-grid" style={{ marginTop: 12 }}>
            <div className="form-field" style={{ flex: 1, minWidth: 200 }}>
              <button type="button" className="btn btn-small btn-secondary" data-link-venue
                disabled={!canWriteShow}
                onClick={() => setForceShowVenue(true)}>
                {t('showDetail.linkVenueOptional')}
              </button>
            </div>
          </div>
        )}
        <div className="form-grid" style={{ marginTop: 16 }}>
          <div className="form-field" style={{ flex: 2, minWidth: 200 }}>
            <label>{t('showDetail.name')}</label>
            <input className="form-input" value={form.name}
              maxLength={120}
              disabled={!canWriteShow}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.name ? '' : t(showTypeProfile(form.event_type).namePhKey || 'showDetail.namePh')} />
          </div>
          <div className="form-field" style={{ flex: 1, minWidth: 150 }}>
            <label>{t('showDetail.band')}</label>
            {/* 2026-11-03 : le groupe n'est CHOISI qu'à la création. Sur une date
                existante, tout en dépend (rider, équipe, tournée, finance, droits) :
                un menu déroulant y invitait à « déplacer » un show d'un groupe à
                l'autre par mégarde. On affiche donc le groupe en lecture seule. */}
            {isNew ? (
              <select className="form-input" value={form.band_id}
                disabled={!canWriteShow}
                onChange={(e) => setForm({ ...form, band_id: e.target.value })}>
                <option value="">{t('showDetail.selectBand')}</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <input className="form-input" value={bandProfile?.name || form.band_id} readOnly
                title={t('showDetail.bandLocked')} />
            )}
          </div>
        </div>
        
        <div className="form-grid" style={{ marginTop: 16 }}>
          
          <div className="form-field" style={{ flex: 1, minWidth: 200 }}>
            <label>{t('showDetail.tour')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                className="form-input"
                value={form.tour_id || ''}
                disabled={!canWriteShow}
                onChange={(e) => setForm({ ...form, tour_id: e.target.value || null })}
              >
                <option value="">{t('showDetail.standalone')}</option>
                {tours.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button className="btn btn-secondary" type="button" onClick={handleCreateTour} disabled={!canWriteShow}>
                {t('showDetail.newTour')}
              </button>
            </div>
          </div>
        </div>

        <div className="form-field" style={{ marginTop: 16 }}>
          <label>{t('showDetail.notes')}</label>
          <AutoTextarea className="form-input" rows={2} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {form.band_id && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            {/* PERSONNES PAR TYPE (2026-10-17) : le titre et le hint viennent du profil
                (performers/travelers/involved/concerned) — un travel day demande « qui voyage ». */}
            <h4 style={{ margin: '0 0 4px' }}>{t(`showDetail.peopleOnShow_${showTypeProfile(form.event_type).people}`)}</h4>
            <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 10px' }}>
              {t(`showDetail.peopleHint_${showTypeProfile(form.event_type).people}`)}
            </p>
            {conflicts.length > 0 && (
              <div
                role="alert"
                style={{
                  border: '1px solid var(--warning)',
                  background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
                  borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('availability.conflictTitle')}</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--font-sm)' }}>
                  {conflicts.map((c) => {
                    const member = [...musicians, ...crew].find((m) => String(m.id) === c.member_id);
                    const name = member ? displayName(member) : c.member_id;
                    return c.conflicts.map((cc, i) => {
                      const key = `${c.member_id}-${i}-${cc.type}`;
                      if (cc.type === 'unavailable') {
                        const range = cc.date_from === cc.date_to ? cc.date_from : `${cc.date_from} → ${cc.date_to}`;
                        const reasonPart = cc.reason ? ` — ${cc.reason}` : '';
                        return (
                          <li key={key}>
                            {t('availability.unavailableLine', { name, range, reason: reasonPart })}
                          </li>
                        );
                      }
                      return (
                        <li key={key}>
                          {t('availability.doubleBookedLine', { name, show: cc.other_show_name, date: cc.date })}
                        </li>
                      );
                    });
                  })}
                </ul>
              </div>
            )}
            {/* Update musicians & crew — on its OWN line above the Musicians
                field (user 2026-10-23). Saves the ticked selection AND rebuilds
                the rider's requirements from the new line-up; the shared rider
                (advancing tech link + /share/rider) reads riders.requirements
                live, so it reflects the rebuild immediately. */}
            {rider && canWriteShow && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  data-update-people
                  disabled={regenerating}
                  title={t('showDetail.updatePeopleTitle')}
                  onClick={updatePeopleOnShow}
                >
                  {regenerating ? t('showDetail.updatingPeople') : t('showDetail.updatePeopleOnShow')}
                </button>
              </div>
            )}
            {/* Deux listes EMPILÉES pleine largeur (U2, retour utilisateur) :
                côte à côte en 2 colonnes, les 8 noms de crew étaient tassés dans
                une demi-largeur. Crew passe sous Musicians. */}
            <div data-people-lists style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 }}>
              <div className="form-field" style={{ minWidth: 0 }}>
                <label>{t('showDetail.musicians', { count: musicians.length })}</label>
                <div className="musician-inline-list">
                  {musicians.length === 0 ? (
                    <span style={{ color: 'var(--text-dim)' }}>{t('showDetail.noMusicians')}</span>
                  ) : musicians.map((m) => (
                    <label key={m.id} className="musician-checkbox">
                      <input type="checkbox" checked={selMus.has(m.id)} onChange={() => toggleMus(m.id)} />
                      {displayName(m)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-field" style={{ minWidth: 0 }}>
                <label>{t('showDetail.crew', { count: crew.length })}</label>
                <div className="musician-inline-list">
                  {crew.length === 0 ? (
                    <span style={{ color: 'var(--text-dim)' }}>{t('showDetail.noCrew')}</span>
                  ) : crew.map((c) => (
                    <label key={c.id} className="musician-checkbox">
                      <input type="checkbox" checked={selCrew.has(c.id)} onChange={() => toggleCrew(c.id)} />
                      {displayName(c)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advancing: promoter share for THIS show's rider. Shown once a rider
          exists for the show, between the show card and the tabbed sections. */}
      {rider && !isNew && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>{t('showDetail.advancingHeading')}</h3>
          <p style={{ color: 'var(--text-dim)', margin: '0 0 10px', fontSize: '0.85rem' }}>
            <Trans i18nKey="showDetail.advancingHint" components={{ strong: <strong /> }}>
              Generate a public link so the promoter can validate the technical requests of <strong>this show</strong> — transport, people and requirements may differ from the band default.
            </Trans>
          </p>
          {shareEnabled && shareToken ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  className="form-input"
                  readOnly
                  value={shareUrl ?? ''}
                  style={{ flex: 1, minWidth: 240 }}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button className="btn" onClick={handleCopyShare}>{shareCopied ? t('advancing.copied') : t('advancing.copy')}</button>
              </div>
              <a className="btn btn-secondary" href={shareUrl ?? '#'} target="_blank" rel="noopener noreferrer" style={{ width: 'fit-content' }}>
                {t('showDetail.openPublicPage')}
              </a>
              {shareHasPassword && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  🔒 {t('showDetail.passwordProtected')}
                </span>
              )}
              <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', margin: 0 }}>
                {t('showDetail.promoterHint')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <PasswordInput
                className="form-input"
                value={sharePassword}
                placeholder={t('showDetail.passwordOptionalPh')}
                containerStyle={{ minWidth: 180, flex: '0 1 220px' }}
                onChange={(e) => setSharePassword(e.target.value)}
              />
              <button className="btn btn-primary" onClick={enableShare}>
                {t('showDetail.enableSharing')}
              </button>
            </div>
          )}
        </div>
      )}

      {!isNew && (
        <>
          {/* Tabbed layout — sticky so the tabs stay aligned at top even for
              short-content tabs (Setlist/Checklist/Files), where the page is
              too short to scroll the bar to the top. (2026-08-14) */}
          <div ref={tabBarRef} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', scrollMarginTop: 12, position: 'sticky', top: 0, zIndex: 20, padding: '8px 0', background: 'var(--bg)' }}>
            {/* PROFIL PAR TYPE (2026-10-17, lib/showTypes.ts) : les onglets sans
                sens pour ce type sont masqués (ex. Tech/Setlist/Advancing/Finance
                sur un travel day). Les données restent intactes. */}
            {(showTypeProfile(form.event_type).tabs as readonly ('day' | 'logistics' | 'tech' | 'setlist' | 'checklist' | 'files' | 'finance' | 'advancing')[])
              .filter((tab) => tab !== 'finance' || financeAccess)
              .map((tab) => (
              <button
                key={tab}
                className="btn"
                style={{
                  flex: '0 0 auto',
                  background: activeTab === tab ? 'var(--accent)' : 'var(--card)',
                  color: activeTab === tab ? 'var(--on-accent)' : 'var(--text)',
                  border: 'none',
                }}
                onClick={() => selectTab(tab)}
              >
                {tab === 'day' ? t('showDetail.tabDay') : tab === 'tech' ? t('showDetail.tabTech') : tab === 'logistics' ? t('showDetail.tabLogistics') : tab === 'files' ? t('showDetail.tabFiles') : tab === 'finance' ? t('showDetail.tabFinance') : tab === 'checklist' ? t('showDetail.tabChecklist') : tab === 'advancing' ? t('showDetail.tabAdvancing') : t('showDetail.tabSetlist')}
              </button>
            ))}
          </div>

          {/* Tab content — min-height ensures short tabs (Setlist/Checklist/Files)
              produce a page tall enough that scrolling past the tab bar makes the
              sticky bar pin at the top, so every tab aligns the same way.
              (2026-08-14) */}
          <div style={{ minHeight: '100vh' }} data-show-tabcontent>

          {/* Logistics (Logistics tab): guestlist + flights + accommodation */}
          {activeTab === 'logistics' && (
            <LogisticsTab
              guests={guests}
              newGuest={newGuest}
              setNewGuest={setNewGuest}
              addGuest={addGuest}
              updateGuest={updateGuest}
              removeGuest={removeGuest}
              canWriteGuests={canWriteGuests}
              realId={realId}
              members={logisticsMembers}
              flightNotes={form.flight_notes || ''}
              onFlightNotesChange={(v) => {
                setForm((f) => ({ ...f, flight_notes: v }));
                if (realId && canWriteShow) apiUpdateShow(realId, { flight_notes: v }).then(() => reportSaveOk('show')).catch((e: any) => reportSaveError('show', e));
              }}
              accommodations={accommodations}
              accSearch={accSearch}
              searchAccommodation={searchAccommodation}
              accResults={accResults}
              setAccSearch={setAccSearch}
              setAccResults={setAccResults}
              addAccommodation={addAccommodation}
              addAccommodationFromResult={addAccommodationFromResult}
              updateAccommodation={updateAccommodation}
              deleteAccommodation={deleteAccommodation}
              roomingFor={roomingFor}
              upsertRooming={upsertRooming}
              clearRooming={clearRooming}
              // Lot A2 (dette A1): the show's OWN band, so TransportSection's gate
              // is decided on the edited entity's band and not on the active one.
              bandId={form.band_id}
              // A global read-only account keeps the tab visible but its inputs
              // are frozen — the writes behind them are already cut here.
              canWrite={canWriteShow}
            />
          )}

          {/* Files (Files tab) — show-scoped media/plans/misc */}
          {activeTab === 'files' && realId && form.band_id && (
            <FilesTab
              bandId={String(form.band_id)}
              showId={Number(realId)}
              venueId={form.venue_id}
              venues={venues}
            />
          )}

          {/* Tech Rider (Tech tab) */}
          {activeTab === 'tech' && (
            <TechTab
              rider={rider}
              transport={transport}
              changeTransport={changeTransport}
              stagePlots={stagePlots}
              selectedStagePlotId={selectedStagePlotId}
              setSelectedStagePlotId={setSelectedStagePlotId}
              musicians={musicians}
              selMus={selMus}
              toggleMus={toggleMus}
              crew={crew}
              selCrew={selCrew}
              toggleCrew={toggleCrew}
              createRider={createRider}
              realId={realId || ''}
              bandId={form.band_id}
              updateRiderSel={updateRiderSel}
              updatePeopleOnShow={updatePeopleOnShow}
              regeneratingPeople={regenerating}
              openPdf={openPdf}
              downloadPdf={downloadPdf}
              generating={generating}
              deleteRider={deleteRider}
              showRoles={showRoles}
              resetShowRoles={resetShowRoles}
              roleSaving={roleSaving}
              toggleShowRole={toggleShowRole}
              setBacklineProvider={setBacklineProvider}
              backlineProviders={backlineProviders}
              pdfDoc={pdfDoc}
            />
          )}

          {/* Setlist assignment — which setlist is played for this show (Setlist tab) */}
          {activeTab === 'setlist' && (
            <SetlistTab
              setlistId={setlistId}
              playedSetlistId={playedSetlistId}
              setlists={setlists}
              assigning={assigning}
              assignSetlist={assignSetlist}
              assignPlayed={assignPlayed}
              copyingPlayed={copyingPlayed}
              copyPlannedAsPlayed={copyPlannedAsPlayed}
              editingPlayed={editingPlayed}
              setEditingPlayed={setEditingPlayed}
              playedSongs={playedSongs}
              setPlayedSongs={setPlayedSongs}
              savePlayedSongs={savePlayedSongs}
              savingPlayed={savingPlayed}
              loadPlayedSongs={loadPlayedSongs}
              exportSetlistPdf={exportSetlistPdf}
              exportingSetlist={exportingSetlist}
              setlistPdfUrl={setlistPdfUrl}
              setlistPdfDoc={setlistPdfDoc}
              exportShowIcs={exportShowIcs}
              bandId={form.band_id}
              showId={realId}
            />
          )} {/* end Setlist tab */}

          {/* Checklist (dedicated tab) — reusable band checklist for this show */}
          {activeTab === 'checklist' && (
            <ChecklistTab
              checklistId={checklistId}
              checklists={checklists}
              setChecklists={setChecklists}
              assigning={assigning}
              assignChecklist={assignChecklist}
              copyChecklistForShow={copyChecklistForShow}
              checklistPdfDoc={checklistPdfDoc}
              bandId={form.band_id}
              showId={realId}
              canWrite={canWriteShow}
              members={allMembers}
            />
          )} {/* end Checklist tab */}

          {/* Accommodation (Logistics tab) */}
          {/* ---- Finance (Finance tab) — only reachable with finance access ---- */}
          {activeTab === 'finance' && financeAccess && (
            <FinanceTab
              form={form}
              setField={(k: string, v: any) => setField(k as any, v)}
              expenses={expenses}
              addExpense={addExpense}
              updateExpense={updateExpense}
              removeExpense={removeExpense}
              perDiemTotal={perDiemTotal}
              expensesByCurrency={expensesByCurrency}
              netInDealCurrency={netInDealCurrency}
              defaultCurrency={bandCurrency}
              fees={fees}
              feesByCurrency={feesByCurrency}
              showMembers={teamMembers.map((m) => ({ id: m.id, name: m.name }))}
              addFee={addFee}
              updateFee={updateFee}
              removeFee={removeFee}
              bulkFees={bulkFees}
              // Lot A1: these blocks are read-AND-write — a global read-only account is
              // frozen unless it ALSO holds the capability to write the block, so
              // the write gate is folded into the very flag that enables the
              // block's controls (the server mounts these 3 capabilities ahead of
              // its global read-only guard, hence the AND). Nothing changes for a
              // normal owner/editor: both factors are already true for them.
              canExpenses={canExpenses && canWriteExpenses}
              canFees={canFees && canWriteFees}
              // Lot A2 (dette A1): the DEAL block holds SHOW-record columns, whose
              // write path is `PUT /api/shows/:id` (mounted AFTER the server's
              // read-only guard) — so a read-only account that legitimately holds
              // `finance.fees` sees the block but must not be able to type into it.
              // The fees/expenses ROWS stay writable: they go through the delegated
              // `/api/shows/:id/fees` + `/expenses` families.
              // (Pre-existing gap, reported & NOT introduced here: the Save payload
              // and the core autosave below never send the deal columns, so the
              // block is inert for every account today.)
              canWriteShow={canWriteShow}
            />
          )}{/* end Finance tab */}

          {/* ---- Advancing (Advancing tab): manager-side hub ---- */}
          {activeTab === 'advancing' && (
            <AdvancingTab
              showId={realId}
              bandId={form.band_id}
              onOpenRider={() => selectTab('tech')}
              riderPdfLoader={buildRiderDocDef}
            />
          )}{/* end Advancing tab */}

              {/* Day Sheet tab */}
          {activeTab === 'day' && (
            <DayTab
              form={form}
              setForm={(patch) => setForm((f) => ({ ...f, ...patch }))}
              realId={realId}
              exportDaysheet={exportDaysheet}
              exporting={daysheeting}
              venue={venues.find((v) => String(v.id) === String(form.venue_id)) || null}
              bandCountry={profiles.find((p) => p.id === form.band_id)?.country || ''}
              accommodationCountry={accommodations[0]?.country || ''}
              bandHomeCity={bandHomeCity}
              daysheetUrl={daysheetUrl}
              daysheetDoc={daysheetDoc}
              bandId={form.band_id}
              // Lot A1: was `my_role ∈ {editor, owner}` — true for a global
              // read-only account with an editor band role, which is exactly the
              // bug. The capability-aware gate is now the single source of truth
              // for the whole Day tab (itinerary, daysheet reminders, tasks).
              canWrite={canWriteShow}
            />
          )}

          </div> {/* end tab content */}

        </>
      )}
      </>
      )}
    </div>
  );
}
